'use client';

import {
  rectContainsPoint,
  rectCenter,
  type MysteryRoomAnchorKind,
  type MysteryRoomCollisionShape,
  type MysteryRoomCustomObjectKind,
  type MysteryRoomCustomObjectDefinition,
  type MysteryRoomCustomObjectShape,
  type MysteryRoomDoorDefinition,
  type MysteryRoomHazardDefinition,
  type MysteryRoomIncidentAnchorDefinition,
  type MysteryRoomIncidentAnchorKind,
  type MysteryRoomInteractableDefinition,
  type MysteryRoomMapDefinition,
  type MysteryRoomNpcBehaviorProfile,
  type MysteryRoomNpcFaction,
  type MysteryRoomNpcRole,
  type MysteryRoomRect,
  type MysteryRoomResourceSourceKind,
  type MysteryRoomSegment,
  type MysteryRoomSoundDefinition,
  type MysteryRoomSpawnSlotDefinition,
  type MysteryRoomTriggerAction,
  type MysteryRoomVec2,
  type MysteryRoomWorkAnchorDefinition,
} from '../runtime/world/mapTypes';
import {
  MYSTERY_ROOM_RESOURCE_DEFINITIONS,
  MYSTERY_ROOM_RESOURCE_SOURCE_DEFINITIONS,
  mysteryRoomResourceItemId,
} from '../runtime/world/resourceDefinitions';
import {
  MYSTERY_ROOM_DEFAULT_ACCESS_CARD_ASSET,
  MYSTERY_ROOM_FLASHLIGHT_ASSETS,
  MYSTERY_ROOM_GLOCK19_ASSETS,
  getMysteryRoomAccessLevelForCardLevel,
} from '../runtime/world/itemAssets';
import {
  getBuiltinMysteryRoomMapDefinitions,
  normalizeMysteryRoomMapDefinition,
} from '../runtime/world/mapRegistry';
import { MYSTERY_ROOM_STANDARD_AMBIENT_SOUND_DEFINITIONS } from '../runtime/world/ambientLibrary';

/** Звуки для новых черновиков редактора (шестерка фонов — см. ambientLibrary). */
const MYSTERY_ROOM_EDITOR_DEFAULT_SOUND_DEFINITIONS: MysteryRoomSoundDefinition[] = [
  { id: 'footstep_soft', label: 'Шаги по лабораторному полу', kind: 'footstep' },
  { id: 'impact_metal', label: 'Металлический удар', kind: 'impact' },
  { id: 'alarm_siren', label: 'Сирена тревоги', kind: 'alarm' },
  ...MYSTERY_ROOM_STANDARD_AMBIENT_SOUND_DEFINITIONS.map((def) => ({ ...def })),
];

export type MysteryRoomEditorTool =
  | 'select'
  | 'marquee'
  | 'pan'
  | 'room'
  | 'wall'
  | 'door'
  | 'table'
  | 'object'
  | 'flashlight'
  | 'access_card'
  | 'pistol'
  | 'quest_item'
  | 'resource_bush'
  | 'resource_scrap'
  | 'resource_rock'
  | 'collision'
  | 'light'
  | 'switch'
  | 'trigger'
  | 'sound'
  | 'hazard'
  | 'incident'
  | 'work'
  | 'spawn'
  | 'bot'
  | 'boss'
  | 'nav';

export type MysteryRoomEditorSelectionType =
  | 'room'
  | 'wall'
  | 'door'
  | 'customObject'
  | 'light'
  | 'interactable'
  | 'trigger'
  | 'soundSource'
  | 'hazard'
  | 'incidentAnchor'
  | 'workAnchor'
  | 'navNode'
  | 'spawn'
  | 'npc';

export type MysteryRoomEditorSelection = {
  type: MysteryRoomEditorSelectionType;
  id: string;
};

export type MysteryRoomEditorTransformAction =
  | 'rotate_left'
  | 'rotate_right'
  | 'length_decrease'
  | 'length_increase'
  | 'thickness_decrease'
  | 'thickness_increase'
  | 'radius_decrease'
  | 'radius_increase';

export type MysteryRoomEditorTransformHandle =
  | 'rotate'
  | 'length'
  | 'thickness'
  | 'radius'
  | `edge:${number}`
  | `side:${MysteryRoomEditorRectSide}`
  | `wallEndpoint:${MysteryRoomEditorWallEndpoint}`;

export type MysteryRoomEditorRectSide = 'top' | 'right' | 'bottom' | 'left';
export type MysteryRoomEditorWallEndpoint = 'start' | 'end';

export type MysteryRoomEditorCreateResult = {
  map: MysteryRoomMapDefinition;
  selection: MysteryRoomEditorSelection;
};

export type MysteryRoomEditorMultiCreateResult = {
  map: MysteryRoomMapDefinition;
  selections: MysteryRoomEditorSelection[];
};

export type MysteryRoomEditorResourceGenerationOptions = {
  seed: string;
  bushCount: number;
  scrapCount: number;
  rockCount: number;
};

export type MysteryRoomEditorTemplate = {
  id: string;
  title: string;
  description: string;
  badge: string;
  createMap: () => MysteryRoomMapDefinition;
};

export const MR_MAP_ASSET_LIMIT_BYTES = 100_000_000;
export const MR_MAP_IMAGE_ASSET_EXTENSIONS = ['png', 'gif', 'webp', 'jpg', 'jpeg'] as const;
export const MR_MAP_AUDIO_ASSET_EXTENSIONS = ['mp3', 'wav', 'ogg', 'oga', 'opus', 'm4a', 'aac', 'weba', 'webm'] as const;
export const MR_MAP_IMAGE_FORMAT_LABEL = 'PNG, GIF, WebP, JPG';
export const MR_MAP_AUDIO_FORMAT_LABEL = 'MP3, WAV, OGG/Opus, M4A/AAC, WebM audio';
export const MR_MAP_ASSET_ACCEPT = [
  ...MR_MAP_IMAGE_ASSET_EXTENSIONS.map((extension) => `.${extension}`),
  '.waw',
  ...MR_MAP_AUDIO_ASSET_EXTENSIONS.map((extension) => `.${extension}`),
  'image/png',
  'image/gif',
  'image/webp',
  'image/jpeg',
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/ogg',
  'audio/opus',
  'audio/mp4',
  'audio/aac',
  'audio/webm',
].join(',');
export const MR_EDITOR_DEFAULT_CANVAS_WIDTH = 12_000;
export const MR_EDITOR_DEFAULT_CANVAS_HEIGHT = 8_000;
export const MR_EDITOR_CANVAS_PADDING = 1_600;
export const MR_EDITOR_LAYER_MIN = 0;
export const MR_EDITOR_LAYER_MAX = 20;
export const MR_EDITOR_WALL_NODE_SNAP_RADIUS = 18;
export const MR_EDITOR_WALL_SEGMENT_SNAP_RADIUS = 14;
export const MR_CUSTOM_OBJECT_MIN_POINTS = 1;
export const MR_CUSTOM_OBJECT_MAX_POINTS = 32;
export const MR_EDITOR_DEFAULT_OBJECT_WIDTH = 60;
export const MR_EDITOR_DEFAULT_OBJECT_HEIGHT = 40;
export const MR_EDITOR_DEFAULT_OBJECT_INNER_PADDING = 1;
export const MR_EDITOR_DEFAULT_LAYERS = {
  floor: 0,
  wall: 1,
  door: 2,
  furniture: 2,
  item: 5,
  interactable: 7,
  trigger: 8,
  sound: 9,
  light: 20,
} as const;

type MysteryRoomEditorResourceTool = Extract<
  MysteryRoomEditorTool,
  'resource_bush' | 'resource_scrap' | 'resource_rock'
>;

const EDITOR_RESOURCE_TOOL_SOURCE_KIND: Record<MysteryRoomEditorResourceTool, MysteryRoomResourceSourceKind> = {
  resource_bush: 'bush',
  resource_scrap: 'scrap',
  resource_rock: 'rock',
};

type LayerableSelectionType =
  | 'wall'
  | 'door'
  | 'customObject'
  | 'light'
  | 'interactable'
  | 'trigger'
  | 'soundSource';

export function createBlankMysteryRoomEditorMap(title = 'Пустая лаборатория'): MysteryRoomMapDefinition {
  const room = { x: 5200, y: 3600, width: 960, height: 640 };
  const roomCenter = rectCenter(room);
  return normalizeEditableMap({
    id: `draft_${slugify(title)}_${Date.now().toString(36)}`,
    title,
    width: MR_EDITOR_DEFAULT_CANVAS_WIDTH,
    height: MR_EDITOR_DEFAULT_CANVAS_HEIGHT,
    spawn: { x: room.x + 200, y: room.y + 200 },
    spawnZoneId: 'main_lab',
    spawnSlots: [
      { x: room.x + 200, y: room.y + 200, room: 'main_lab', zoneId: 'main_lab', label: 'Игрок 1' },
    ],
    visibilityRadius: 760,
    rooms: [
      {
        id: 'main_lab',
        title: 'Основная зона',
        accessLevel: 'public',
        floor: [room],
        color: 0x334155,
        labelPoint: { x: roomCenter.x, y: room.y + 50 },
      },
    ],
    doors: [],
    walls: [
      { start: { x: room.x, y: room.y }, end: { x: room.x + room.width, y: room.y } },
      { start: { x: room.x + room.width, y: room.y }, end: { x: room.x + room.width, y: room.y + room.height } },
      { start: { x: room.x + room.width, y: room.y + room.height }, end: { x: room.x, y: room.y + room.height } },
      { start: { x: room.x, y: room.y + room.height }, end: { x: room.x, y: room.y } },
    ],
    occluders: [],
    interactables: [],
    workAnchors: [],
    navNodes: [
      { id: 'nav_main', zoneId: 'main_lab', position: roomCenter, kind: 'hub' },
    ],
    navEdges: [],
    incidentAnchors: [],
    patrolRoutes: [],
    npcs: [],
    lights: [
      {
        id: 'light_main',
        zoneId: 'main_lab',
        position: { x: roomCenter.x, y: roomCenter.y - 20 },
        radius: 520,
        color: 0xfef3c7,
        intensity: 0.82,
      },
    ],
    hazards: [],
    props: [],
    customObjects: [],
    triggers: [],
    soundDefinitions: [...MYSTERY_ROOM_EDITOR_DEFAULT_SOUND_DEFINITIONS],
    soundSources: [
      {
        id: 'ambient_main_lab',
        label: 'Фон основной зоны',
        zoneId: 'main_lab',
        position: { x: roomCenter.x, y: roomCenter.y },
        soundId: 'ambient_track_1',
        radius: Math.max(room.width, room.height) + 560,
        volume: 0.3,
        loop: true,
      },
    ],
  });
}

export function createCompactServerWingTemplate(): MysteryRoomMapDefinition {
  const map = createBlankMysteryRoomEditorMap('Серверное крыло');
  return normalizeEditableMap({
    ...map,
    width: 2800,
    height: 1700,
    spawn: { x: 320, y: 460 },
    spawnZoneId: 'airlock',
    spawnSlots: [
      { x: 320, y: 460, room: 'airlock', zoneId: 'airlock', label: 'Игрок 1' },
      { x: 380, y: 520, room: 'airlock', zoneId: 'airlock', label: 'Игрок 2' },
    ],
    rooms: [
      {
        id: 'airlock',
        title: 'Шлюз',
        accessLevel: 'public',
        floor: [{ x: 160, y: 260, width: 440, height: 420 }],
        color: 0x334155,
        labelPoint: { x: 380, y: 318 },
      },
      {
        id: 'corridor',
        title: 'Коридор',
        accessLevel: 'public',
        floor: [{ x: 600, y: 400, width: 720, height: 180 }],
        color: 0x1f2937,
        labelPoint: { x: 960, y: 450 },
      },
      {
        id: 'server',
        title: 'Серверная',
        accessLevel: 'server',
        floor: [{ x: 1320, y: 260, width: 680, height: 520 }],
        color: 0x312e81,
        labelPoint: { x: 1660, y: 320 },
      },
    ],
    walls: [
      { start: { x: 160, y: 260 }, end: { x: 600, y: 260 } },
      { start: { x: 160, y: 680 }, end: { x: 600, y: 680 } },
      { start: { x: 160, y: 260 }, end: { x: 160, y: 680 } },
      { start: { x: 600, y: 400 }, end: { x: 1320, y: 400 } },
      { start: { x: 600, y: 580 }, end: { x: 1320, y: 580 } },
      { start: { x: 1320, y: 260 }, end: { x: 2000, y: 260 } },
      { start: { x: 2000, y: 260 }, end: { x: 2000, y: 780 } },
      { start: { x: 2000, y: 780 }, end: { x: 1320, y: 780 } },
    ],
    doors: [
      {
        id: 'door_server',
        fromZoneId: 'corridor',
        toZoneId: 'server',
        requiredAccess: 'server',
        panel: { x: 1284, y: 452, width: 72, height: 76 },
        trigger: { x: 1210, y: 380, width: 220, height: 220 },
        orientation: 'vertical',
      },
    ],
    navNodes: [
      { id: 'nav_airlock', zoneId: 'airlock', position: { x: 380, y: 470 }, kind: 'hub' },
      { id: 'nav_corridor', zoneId: 'corridor', position: { x: 960, y: 490 }, kind: 'hub' },
      { id: 'nav_server', zoneId: 'server', position: { x: 1660, y: 520 }, kind: 'work' },
    ],
    navEdges: [
      { id: 'edge_airlock_corridor', fromNodeId: 'nav_airlock', toNodeId: 'nav_corridor', distanceCost: 580 },
      { id: 'edge_corridor_server', fromNodeId: 'nav_corridor', toNodeId: 'nav_server', distanceCost: 720, doorId: 'door_server', requiredAccess: 'server' },
    ],
    lights: [
      { id: 'light_airlock', zoneId: 'airlock', position: { x: 380, y: 450 }, radius: 380, color: 0xe0f2fe, intensity: 0.74 },
      { id: 'light_corridor', zoneId: 'corridor', position: { x: 960, y: 490 }, radius: 470, color: 0xfef3c7, intensity: 0.7 },
      { id: 'light_server', zoneId: 'server', position: { x: 1660, y: 520 }, radius: 560, color: 0xa5b4fc, intensity: 0.8 },
    ],
    customObjects: [
      createTemplateObject('rack_a', 'server', 'Серверный шкаф', 'equipment', { x: 1450, y: 340, width: 90, height: 300 }, 0x0f172a),
      createTemplateObject('rack_b', 'server', 'Серверный шкаф', 'equipment', { x: 1580, y: 340, width: 90, height: 300 }, 0x0f172a),
      createTemplateObject('console_a', 'server', 'Консоль', 'equipment', { x: 1740, y: 560, width: 180, height: 76 }, 0x2563eb),
    ],
    soundSources: [
      {
        id: 'sound_ambient_server',
        label: 'Фон серверной',
        zoneId: 'server',
        position: { x: 1820, y: 340 },
        soundId: 'ambient_track_4',
        radius: 720,
        volume: 0.3,
        loop: true,
      },
    ],
  });
}

export function createEmergencyLabTemplate(): MysteryRoomMapDefinition {
  const map = createBlankMysteryRoomEditorMap('Аварийная лаборатория');
  return normalizeEditableMap({
    ...map,
    width: 3000,
    height: 1800,
    spawn: { x: 360, y: 520 },
    spawnZoneId: 'entry',
    spawnSlots: [
      { x: 360, y: 520, room: 'entry', zoneId: 'entry', label: 'Игрок 1' },
      { x: 420, y: 580, room: 'entry', zoneId: 'entry', label: 'Игрок 2' },
    ],
    rooms: [
      { id: 'entry', title: 'Вход', accessLevel: 'public', floor: [{ x: 180, y: 360, width: 420, height: 360 }], color: 0x334155, labelPoint: { x: 390, y: 420 } },
      { id: 'lab', title: 'Лаборатория', accessLevel: 'lab', floor: [{ x: 680, y: 280, width: 760, height: 560 }], color: 0x0f766e, labelPoint: { x: 1060, y: 345 } },
      { id: 'reactor', title: 'Реакторный блок', accessLevel: 'reactor', floor: [{ x: 1520, y: 240, width: 680, height: 620 }], color: 0x7f1d1d, labelPoint: { x: 1860, y: 310 } },
    ],
    walls: [
      { start: { x: 180, y: 360 }, end: { x: 600, y: 360 } },
      { start: { x: 180, y: 720 }, end: { x: 600, y: 720 } },
      { start: { x: 680, y: 280 }, end: { x: 1440, y: 280 } },
      { start: { x: 680, y: 840 }, end: { x: 1440, y: 840 } },
      { start: { x: 1520, y: 240 }, end: { x: 2200, y: 240 } },
      { start: { x: 2200, y: 240 }, end: { x: 2200, y: 860 } },
      { start: { x: 2200, y: 860 }, end: { x: 1520, y: 860 } },
    ],
    doors: [
      { id: 'door_lab', fromZoneId: 'entry', toZoneId: 'lab', requiredAccess: 'lab', panel: { x: 606, y: 516, width: 74, height: 72 }, trigger: { x: 540, y: 450, width: 210, height: 210 }, orientation: 'vertical' },
      { id: 'door_reactor', fromZoneId: 'lab', toZoneId: 'reactor', requiredAccess: 'reactor', panel: { x: 1444, y: 510, width: 76, height: 80 }, trigger: { x: 1370, y: 430, width: 230, height: 230 }, orientation: 'vertical' },
    ],
    lights: [
      { id: 'light_entry', zoneId: 'entry', position: { x: 390, y: 540 }, radius: 380, color: 0xe0f2fe, intensity: 0.75 },
      { id: 'light_lab', zoneId: 'lab', position: { x: 1060, y: 560 }, radius: 560, color: 0xccfbf1, intensity: 0.82 },
      { id: 'light_reactor', zoneId: 'reactor', position: { x: 1860, y: 560 }, radius: 600, color: 0xfca5a5, intensity: 0.7 },
    ],
    triggers: [
      {
        id: 'trigger_reactor_alarm',
        label: 'Тревога реактора',
        zoneId: 'reactor',
        rect: { x: 1740, y: 420, width: 240, height: 200 },
        event: 'onEnter',
        repeatable: false,
        cooldownMs: 0,
        actions: [{ kind: 'set_alarm', enabled: true }, { kind: 'spawn_smoke', position: { x: 1850, y: 600 }, radius: 260 }],
      },
    ],
    customObjects: [
      createTemplateObject('lab_table_a', 'lab', 'Стол анализов', 'table', { x: 820, y: 470, width: 230, height: 88 }, 0x475569),
      createTemplateObject('reactor_core_a', 'reactor', 'Реактор', 'equipment', { x: 1770, y: 500, width: 180, height: 180 }, 0xdc2626),
    ],
    soundSources: [
      {
        id: 'ambient_emergency',
        label: 'Фон аварийной лаборатории',
        zoneId: 'lab',
        position: { x: 1180, y: 520 },
        soundId: 'ambient_track_1',
        radius: 1300,
        volume: 0.3,
        loop: true,
      },
    ],
  });
}

export function getMysteryRoomEditorTemplates(): MysteryRoomEditorTemplate[] {
  const officialTemplates: MysteryRoomEditorTemplate[] = getBuiltinMysteryRoomMapDefinitions().map((map) => ({
    id: `official-${map.id}`,
    title: `${map.title} как шаблон`,
    description: 'Официальная карта со всеми комнатами, объектами, дверями, светом, навигацией и игровыми настройками.',
    badge: 'official',
    createMap: () => cloneMapForDraft(map, `Черновик: ${map.title}`),
  }));

  return [
    {
      id: 'blank',
      title: 'Пустая карта',
      description: 'Минимальная валидная лаборатория: одна зона, границы, старт, базовый свет и звуковые пресеты.',
      badge: 'empty',
      createMap: () => createBlankMysteryRoomEditorMap('Пустая лаборатория'),
    },
    ...officialTemplates,
    {
      id: 'server-wing',
      title: 'Серверное крыло',
      description: 'Компактный шаблон с закрытой серверной, доступом, дверью, шкафами и фоновой музыкой серверной.',
      badge: 'template',
      createMap: createCompactServerWingTemplate,
    },
    {
      id: 'emergency-lab',
      title: 'Аварийная лаборатория',
      description: 'Три зоны, реакторная дверь, триггер тревоги и дым для проверки игровых событий.',
      badge: 'template',
      createMap: createEmergencyLabTemplate,
    },
  ];
}

export function cloneMapForDraft(base: MysteryRoomMapDefinition, title = 'Новая лаборатория'): MysteryRoomMapDefinition {
  const map = normalizeEditableMap(deepClone(base));
  map.id = `draft_${slugify(title)}_${Date.now().toString(36)}`;
  map.title = title;
  return map;
}

export function normalizeEditableMap(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition {
  const normalized = normalizeMysteryRoomMapDefinition(map);
  const spawnSlots = normalizeEditorSpawnSlots(normalized);
  const primarySpawn = spawnSlots[0] ?? { x: normalized.spawn.x, y: normalized.spawn.y, room: normalized.spawnZoneId };
  const withEditorDefaults = {
    ...normalized,
    spawn: { x: primarySpawn.x, y: primarySpawn.y },
    spawnZoneId: primarySpawn.room ?? primarySpawn.zoneId ?? normalized.spawnZoneId,
    spawnSlots,
    walls: (normalized.walls ?? []).map((wall) => ({ ...wall, layer: normalizeEditorLayer(wall.layer, defaultEditorLayerForSelectionType('wall')) })),
    doors: (normalized.doors ?? []).map((door) => ({ ...door, layer: normalizeEditorLayer(door.layer, defaultEditorLayerForSelectionType('door')) })),
    interactables: (normalized.interactables ?? []).map((entry) => ({
      ...entry,
      defaultEnabled: entry.kind === 'light_switch' ? entry.defaultEnabled ?? true : entry.defaultEnabled,
      layer: normalizeEditorLayer(entry.layer, defaultEditorLayerForSelectionType('interactable')),
    })),
    lights: (normalized.lights ?? []).map((light) => ({ ...light, layer: normalizeEditorLayer(light.layer, defaultEditorLayerForSelectionType('light')) })),
    props: (normalized.props ?? []).map((prop) => ({ ...prop, layer: normalizeEditorLayer(prop.layer, defaultEditorLayerForPropKind(prop.kind)) })),
    customObjects: (normalized.customObjects ?? []).map((object) => ({
      ...object,
      layer: normalizeEditorLayer(object.layer, defaultEditorLayerForCustomObjectKind(object.kind)),
      weightKg: normalizeEditorObjectWeight(object.weightKg),
      shape: normalizeCustomObjectShape(object.shape),
    })),
    triggers: (normalized.triggers ?? []).map((trigger) => ({ ...trigger, layer: normalizeEditorLayer(trigger.layer, defaultEditorLayerForSelectionType('trigger')) })),
    hazards: normalized.hazards ?? [],
    workAnchors: normalized.workAnchors ?? [],
    incidentAnchors: normalized.incidentAnchors ?? [],
    soundDefinitions: normalized.soundDefinitions ?? [],
    soundSources: (normalized.soundSources ?? []).map((source) => ({ ...source, layer: normalizeEditorLayer(source.layer, defaultEditorLayerForSelectionType('soundSource')) })),
  };
  return expandEditableMapCanvas(repairEditableMapSpawn(withEditorDefaults));
}

export function normalizeEditorSpawnSlots(map: MysteryRoomMapDefinition): MysteryRoomSpawnSlotDefinition[] {
  const rawSlots = map.spawnSlots?.length
    ? map.spawnSlots
    : [{ x: map.spawn.x, y: map.spawn.y, room: map.spawnZoneId, zoneId: map.spawnZoneId }];
  const sourceSlots = [
    { ...rawSlots[0], x: map.spawn.x, y: map.spawn.y, room: rawSlots[0]?.room ?? rawSlots[0]?.zoneId ?? map.spawnZoneId },
    ...rawSlots.slice(1),
  ];
  return sourceSlots.map((slot, index) => {
    const zoneId = slot.room ?? slot.zoneId ?? map.spawnZoneId;
    const actor: MysteryRoomSpawnSlotDefinition['actor'] = slot.actor === 'bot' ? 'bot' : 'player';
    const side: MysteryRoomSpawnSlotDefinition['side'] = slot.side === 'B' ? 'B' : 'A';
    const defaultLabelPrefix = actor === 'bot' ? 'Бот' : 'Игрок';
    return {
      x: Math.round(slot.x),
      y: Math.round(slot.y),
      room: zoneId,
      zoneId,
      label: slot.label || `${defaultLabelPrefix} ${index + 1}`,
      actor,
      side,
    };
  });
}

function withEditorSpawnSlots(map: MysteryRoomMapDefinition, spawnSlots: MysteryRoomSpawnSlotDefinition[]): MysteryRoomMapDefinition {
  const normalizedSlots = (spawnSlots.length > 0 ? spawnSlots : [{ x: map.spawn.x, y: map.spawn.y, room: map.spawnZoneId, zoneId: map.spawnZoneId }])
    .map((slot, index) => {
      const point = { x: Math.round(slot.x), y: Math.round(slot.y) };
      const zoneId = slot.room ?? slot.zoneId ?? resolveZoneIdAtPoint(map, point);
      return {
        x: point.x,
        y: point.y,
        room: zoneId,
        zoneId,
        label: slot.label || `${slot.actor === 'bot' ? 'Бот' : 'Игрок'} ${index + 1}`,
        actor: (slot.actor === 'bot' ? 'bot' : 'player') as MysteryRoomSpawnSlotDefinition['actor'],
        side: (slot.side === 'B' ? 'B' : 'A') as MysteryRoomSpawnSlotDefinition['side'],
      };
    });
  const primary = normalizedSlots[0]!;
  return {
    ...map,
    spawn: { x: primary.x, y: primary.y },
    spawnZoneId: primary.room ?? primary.zoneId ?? map.spawnZoneId,
    spawnSlots: normalizedSlots,
  };
}

export function repairEditableMapSpawn(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition {
  const roomsWithFloor = (map.rooms ?? []).filter((room) => room.floor.some((floor) => floor.width > 0 && floor.height > 0));
  if (!roomsWithFloor.length) {
    return map;
  }
  const containingRoom = roomsWithFloor.find((room) => room.floor.some((floor) => rectContainsPoint(floor, map.spawn)));
  if (containingRoom) {
    const next = map.spawnZoneId === containingRoom.id ? map : { ...map, spawnZoneId: containingRoom.id };
    return withEditorSpawnSlots(next, normalizeEditorSpawnSlots(next).map((slot, index) => index === 0 ? { ...slot, room: containingRoom.id, zoneId: containingRoom.id } : slot));
  }
  const fallbackRoom = roomsWithFloor.find((room) => room.id === map.spawnZoneId) ?? roomsWithFloor[0];
  const fallbackFloor = fallbackRoom.floor[0];
  return withEditorSpawnSlots({
    ...map,
    spawn: roundPoint(rectCenter(fallbackFloor)),
    spawnZoneId: fallbackRoom.id,
  }, normalizeEditorSpawnSlots(map).map((slot, index) => index === 0 ? { x: Math.round(rectCenter(fallbackFloor).x), y: Math.round(rectCenter(fallbackFloor).y), room: fallbackRoom.id, zoneId: fallbackRoom.id, label: slot.label } : slot));
}

export function normalizeCustomObjectPointCount(value: number): number {
  if (!Number.isFinite(value)) {
    return 4;
  }
  return Math.max(MR_CUSTOM_OBJECT_MIN_POINTS, Math.min(MR_CUSTOM_OBJECT_MAX_POINTS, Math.round(value)));
}

export function createRegularCustomObjectShape(pointCount: number): MysteryRoomCustomObjectShape {
  const count = normalizeCustomObjectPointCount(pointCount);
  if (count === 1) {
    return { kind: 'polygon', points: [{ x: 0, y: 0 }] };
  }
  if (count === 2) {
    return { kind: 'polygon', points: [{ x: -0.5, y: 0 }, { x: 0.5, y: 0 }] };
  }
  const startAngle = count === 4 ? -Math.PI / 4 : -Math.PI / 2;
  return {
    kind: 'polygon',
    points: Array.from({ length: count }, (_, index) => ({
      x: Math.round(Math.cos(startAngle + (Math.PI * 2 * index) / count) * 0.5 * 1000) / 1000,
      y: Math.round(Math.sin(startAngle + (Math.PI * 2 * index) / count) * 0.5 * 1000) / 1000,
    })),
  };
}

export function getCustomObjectShapePoints(object: MysteryRoomCustomObjectDefinition): MysteryRoomVec2[] {
  if (object.shape?.kind === 'polygon' && object.shape.points.length > 0) {
    return object.shape.points.map((point) => ({ ...point }));
  }
  return [
    { x: -0.5, y: -0.5 },
    { x: 0.5, y: -0.5 },
    { x: 0.5, y: 0.5 },
    { x: -0.5, y: 0.5 },
  ];
}

export function getCustomObjectShapePointCount(object: MysteryRoomCustomObjectDefinition): number {
  return getCustomObjectShapePoints(object).length;
}

export function setCustomObjectShapePointCount(
  object: MysteryRoomCustomObjectDefinition,
  pointCount: number,
): MysteryRoomCustomObjectDefinition {
  return withSyncedObjectCollision({
    ...object,
    shape: createRegularCustomObjectShape(pointCount),
  }, object.rect);
}

export function getCustomObjectShapeWorldPoints(object: MysteryRoomCustomObjectDefinition): MysteryRoomVec2[] {
  const center = rectCenter(object.rect);
  const radians = ((object.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return getCustomObjectShapePoints(object).map((point) => {
    const localX = point.x * object.rect.width;
    const localY = point.y * object.rect.height;
    return {
      x: center.x + localX * cos - localY * sin,
      y: center.y + localX * sin + localY * cos,
    };
  });
}

export function createCustomObjectShapeCollision(
  object: MysteryRoomCustomObjectDefinition,
  rect: MysteryRoomRect = object.rect,
): MysteryRoomCollisionShape {
  const blocksMovement = object.collision.kind === 'none' ? true : object.collision.blocksMovement;
  const blocksSight = object.collision.kind === 'none' ? object.blocksSight : object.collision.blocksSight;
  const objectWithRect = { ...object, rect };
  const points = objectWithRect.shape?.kind === 'polygon' ? getCustomObjectShapeWorldPoints(objectWithRect) : [];
  if (points.length === 1) {
    return {
      kind: 'circle',
      center: points[0],
      radius: Math.max(8, Math.round(Math.min(rect.width, rect.height) * 0.16)),
      blocksMovement,
      blocksSight,
    };
  }
  if (points.length === 2) {
    return {
      kind: 'rect',
      rect: boundsFromPoints(points, Math.max(8, Math.round(Math.min(rect.width, rect.height) * 0.09))),
      blocksMovement,
      blocksSight,
    };
  }
  if (points.length >= 3) {
    return {
      kind: 'polygon',
      points,
      blocksMovement,
      blocksSight,
    };
  }
  return { kind: 'rect', rect: { ...rect }, blocksMovement, blocksSight };
}

function boundsFromPoints(points: MysteryRoomVec2[], padding = 0): MysteryRoomRect {
  const minX = Math.min(...points.map((point) => point.x)) - padding;
  const maxX = Math.max(...points.map((point) => point.x)) + padding;
  const minY = Math.min(...points.map((point) => point.y)) - padding;
  const maxY = Math.max(...points.map((point) => point.y)) + padding;
  return {
    x: Math.round(minX),
    y: Math.round(minY),
    width: Math.max(1, Math.round(maxX - minX)),
    height: Math.max(1, Math.round(maxY - minY)),
  };
}

function normalizeCustomObjectShape(shape: MysteryRoomCustomObjectShape | undefined): MysteryRoomCustomObjectShape | undefined {
  if (!shape) {
    return undefined;
  }
  if (shape.kind === 'rect') {
    return shape;
  }
  const points = (shape.points ?? [])
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
    .slice(0, MR_CUSTOM_OBJECT_MAX_POINTS)
    .map((point) => ({
      x: Math.max(-2, Math.min(2, Math.round(point.x * 1000) / 1000)),
      y: Math.max(-2, Math.min(2, Math.round(point.y * 1000) / 1000)),
    }));
  return points.length > 0 ? { kind: 'polygon', points } : undefined;
}

export function bindPersistedMapId(map: MysteryRoomMapDefinition, mapId: string): MysteryRoomMapDefinition {
  return normalizeEditableMap({
    ...map,
    id: mapId,
  });
}

export type MysteryRoomEditorContentBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
};

export function getEditableMapContentBounds(map: MysteryRoomMapDefinition): MysteryRoomEditorContentBounds {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  const includePoint = (point: MysteryRoomVec2 | null | undefined, radius = 0) => {
    if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return;
    minX = Math.min(minX, point.x - radius);
    minY = Math.min(minY, point.y - radius);
    maxX = Math.max(maxX, point.x + radius);
    maxY = Math.max(maxY, point.y + radius);
  };
  const includeRect = (rect: MysteryRoomRect | null | undefined) => {
    if (!rect) return;
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.width);
    maxY = Math.max(maxY, rect.y + rect.height);
  };
  const includeSegment = (segment: MysteryRoomSegment | null | undefined) => {
    if (!segment) return;
    includePoint(segment.start);
    includePoint(segment.end);
  };

  normalizeEditorSpawnSlots(map).forEach((slot) => includePoint({ x: slot.x, y: slot.y }, 180));
  map.rooms?.forEach((room) => {
    room.floor.forEach(includeRect);
    includePoint(room.labelPoint, 120);
  });
  map.doors?.forEach((door) => {
    includeRect(door.panel);
    includeRect(door.trigger);
  });
  map.walls?.forEach(includeSegment);
  map.occluders?.forEach((occluder) => includeRect(occluder.rect));
  map.interactables?.forEach((entry) => includePoint(entry.anchor, Math.max(80, entry.interactionRadius ?? 0)));
  map.workAnchors?.forEach((anchor) => includePoint(anchor.position, 120));
  map.navNodes?.forEach((node) => includePoint(node.position, 120));
  map.incidentAnchors?.forEach((anchor) => includePoint(anchor.position, 120));
  map.patrolRoutes?.forEach((route) => route.nodes.forEach((node) => includePoint(node, 80)));
  map.npcs?.forEach((npc) => includePoint(npc.spawn, 120));
  map.lights?.forEach((light) => includePoint(light.position, Math.max(120, light.radius ?? 0)));
  map.hazards?.forEach((hazard) => includePoint(hazard.position, Math.max(120, hazard.radius ?? 0)));
  map.props?.forEach((prop) => includeRect(prop.rect));
  map.customObjects?.forEach((object) => {
    includeRect(object.rect);
    if (object.collision.kind === 'rect') includeRect(object.collision.rect);
    if (object.collision.kind === 'circle') includePoint(object.collision.center, object.collision.radius);
    if (object.collision.kind === 'polygon') object.collision.points.forEach((point) => includePoint(point, 24));
  });
  map.triggers?.forEach((trigger) => includeRect(trigger.rect));
  map.soundSources?.forEach((source) => includePoint(source.position, Math.max(120, source.radius ?? 0)));

  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
  };
}

function expandEditableMapCanvas(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition {
  const bounds = getEditableMapContentBounds(map);
  const width = Math.max(
    MR_EDITOR_DEFAULT_CANVAS_WIDTH,
    Math.ceil(map.width || 0),
    Math.ceil(bounds.maxX + MR_EDITOR_CANVAS_PADDING),
  );
  const height = Math.max(
    MR_EDITOR_DEFAULT_CANVAS_HEIGHT,
    Math.ceil(map.height || 0),
    Math.ceil(bounds.maxY + MR_EDITOR_CANVAS_PADDING),
  );
  return {
    ...map,
    width,
    height,
  };
}

export function normalizeEditorLayer(value: number | null | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return clampEditorLayer(fallback);
  }
  return clampEditorLayer(value);
}

export function clampEditorLayer(value: number): number {
  return Math.max(MR_EDITOR_LAYER_MIN, Math.min(MR_EDITOR_LAYER_MAX, Math.round(value)));
}

export function defaultEditorLayerForSelectionType(type: LayerableSelectionType): number {
  switch (type) {
    case 'wall':
      return MR_EDITOR_DEFAULT_LAYERS.wall;
    case 'door':
      return MR_EDITOR_DEFAULT_LAYERS.door;
    case 'customObject':
      return MR_EDITOR_DEFAULT_LAYERS.furniture;
    case 'light':
      return MR_EDITOR_DEFAULT_LAYERS.light;
    case 'interactable':
      return MR_EDITOR_DEFAULT_LAYERS.interactable;
    case 'trigger':
      return MR_EDITOR_DEFAULT_LAYERS.trigger;
    case 'soundSource':
      return MR_EDITOR_DEFAULT_LAYERS.sound;
    default:
      return MR_EDITOR_DEFAULT_LAYERS.furniture;
  }
}

export function defaultEditorLayerForCustomObjectKind(kind: MysteryRoomCustomObjectKind): number {
  switch (kind) {
    case 'badge':
    case 'weapon':
      return MR_EDITOR_DEFAULT_LAYERS.item;
    case 'table':
    case 'wall':
    case 'door':
    case 'equipment':
    case 'decor':
    case 'primitive':
    case 'image':
    default:
      return MR_EDITOR_DEFAULT_LAYERS.furniture;
  }
}

function defaultEditorLayerForPropKind(kind: string): number {
  if (kind === 'floor_grate' || kind === 'warning_stripe') {
    return MR_EDITOR_DEFAULT_LAYERS.floor;
  }
  return MR_EDITOR_DEFAULT_LAYERS.furniture;
}

function isLayerableSelection(selection: MysteryRoomEditorSelection): selection is MysteryRoomEditorSelection & { type: LayerableSelectionType } {
  return (
    selection.type === 'wall' ||
    selection.type === 'door' ||
    selection.type === 'customObject' ||
    selection.type === 'light' ||
    selection.type === 'interactable' ||
    selection.type === 'trigger' ||
    selection.type === 'soundSource' ||
    selection.type === 'hazard' ||
    selection.type === 'incidentAnchor' ||
    selection.type === 'workAnchor'
  );
}

function nextEditorEntityName(map: MysteryRoomMapDefinition, prefix: string): string {
  const values = new Set<string>();
  const add = (value: string | null | undefined) => {
    if (value) values.add(value);
  };

  map.rooms.forEach((room) => {
    add(room.id);
    add(room.title);
  });
  map.doors.forEach((door) => add(door.id));
  map.lights.forEach((light) => {
    add(light.id);
    add(light.label);
  });
  map.interactables.forEach((entry) => {
    add(entry.id);
    add(entry.label);
  });
  map.triggers.forEach((trigger) => {
    add(trigger.id);
    add(trigger.label);
  });
  map.hazards.forEach((hazard) => add(hazard.id));
  map.workAnchors.forEach((anchor) => add(anchor.id));
  map.incidentAnchors.forEach((anchor) => add(anchor.id));
  map.soundDefinitions.forEach((sound) => {
    add(sound.id);
    add(sound.label);
  });
  map.soundSources.forEach((source) => {
    add(source.id);
    add(source.label);
  });
  map.navNodes.forEach((node) => add(node.id));
  map.customObjects.forEach((object) => {
    add(object.id);
    add(object.label);
  });
  map.npcs.forEach((npc) => {
    add(npc.id);
    add(npc.name);
  });
  add(map.spawnZoneId);

  for (let index = 1; index < 10000; index += 1) {
    const candidate = `${prefix}_${index}`;
    if (!values.has(candidate)) return candidate;
  }

  return `${prefix}_${Date.now().toString(36)}`;
}

function duplicateEditorEntityNamePrefix(value: string | null | undefined, fallback: string): string {
  const normalized = (value ?? fallback)
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  const withoutCopySuffix = normalized.replace(/_copy(?:_\d+)?$/i, '');
  const withoutNumericSuffix = withoutCopySuffix.replace(/_\d+$/i, '');
  return withoutNumericSuffix || fallback;
}

function nextDuplicatedEditorEntityName(map: MysteryRoomMapDefinition, value: string | null | undefined, fallback: string): string {
  return nextEditorEntityName(map, duplicateEditorEntityNamePrefix(value, fallback));
}

function editorSelectionKey(selection: MysteryRoomEditorSelection): string {
  return `${selection.type}:${selection.id}`;
}

function uniqueEditorSelections(selections: MysteryRoomEditorSelection[]): MysteryRoomEditorSelection[] {
  const seen = new Set<string>();
  const result: MysteryRoomEditorSelection[] = [];
  selections.forEach((selection) => {
    const key = editorSelectionKey(selection);
    if (seen.has(key)) return;
    seen.add(key);
    result.push(selection);
  });
  return result;
}

function pointBounds(point: MysteryRoomVec2, size: number): MysteryRoomRect {
  return {
    x: point.x - size / 2,
    y: point.y - size / 2,
    width: size,
    height: size,
  };
}

function spawnSlotIndexFromSelectionId(id: string): number {
  if (id === 'spawn') {
    return 0;
  }
  const index = Number(id);
  return Number.isInteger(index) && index >= 0 ? index : 0;
}

function spawnSlotForSelection(map: MysteryRoomMapDefinition, id: string): MysteryRoomSpawnSlotDefinition | null {
  return normalizeEditorSpawnSlots(map)[spawnSlotIndexFromSelectionId(id)] ?? null;
}

export function getEditorSelectionBounds(
  sourceMap: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
): MysteryRoomRect | null {
  if (!selection) return null;
  const map = normalizeEditableMap(sourceMap);
  switch (selection.type) {
    case 'room':
      return map.rooms.find((room) => room.id === selection.id)?.floor[0] ?? null;
    case 'wall': {
      const wall = map.walls[Number(selection.id)];
      if (!wall) return null;
      const minX = Math.min(wall.start.x, wall.end.x);
      const minY = Math.min(wall.start.y, wall.end.y);
      const maxX = Math.max(wall.start.x, wall.end.x);
      const maxY = Math.max(wall.start.y, wall.end.y);
      return { x: minX - 24, y: minY - 24, width: Math.max(48, maxX - minX + 48), height: Math.max(48, maxY - minY + 48) };
    }
    case 'door':
      return map.doors.find((door) => door.id === selection.id)?.panel ?? null;
    case 'customObject':
      return map.customObjects.find((object) => object.id === selection.id)?.rect ?? null;
    case 'light': {
      const light = map.lights.find((entry) => entry.id === selection.id);
      return light ? pointBounds(light.position, 72) : null;
    }
    case 'interactable': {
      const entry = map.interactables.find((item) => item.id === selection.id);
      return entry ? pointBounds(entry.anchor, 68) : null;
    }
    case 'trigger':
      return map.triggers.find((trigger) => trigger.id === selection.id)?.rect ?? null;
    case 'soundSource': {
      const source = map.soundSources.find((entry) => entry.id === selection.id);
      return source ? pointBounds(source.position, 72) : null;
    }
    case 'hazard': {
      const hazard = map.hazards.find((entry) => entry.id === selection.id);
      return hazard ? pointBounds(hazard.position, 72) : null;
    }
    case 'incidentAnchor': {
      const anchor = map.incidentAnchors.find((entry) => entry.id === selection.id);
      return anchor ? pointBounds(anchor.position, 68) : null;
    }
    case 'workAnchor': {
      const anchor = map.workAnchors.find((entry) => entry.id === selection.id);
      return anchor ? pointBounds(anchor.position, 68) : null;
    }
    case 'navNode': {
      const node = map.navNodes.find((entry) => entry.id === selection.id);
      return node ? pointBounds(node.position, 54) : null;
    }
    case 'spawn': {
      const slot = spawnSlotForSelection(map, selection.id);
      return slot ? pointBounds({ x: slot.x, y: slot.y }, 72) : null;
    }
    case 'npc': {
      const npc = map.npcs.find((entry) => entry.id === selection.id);
      return npc ? pointBounds(npc.spawn, Math.max(72, (npc.radius ?? 16) * 2 + 28)) : null;
    }
    default:
      return null;
  }
}

export function getEditorSelectionsBounds(
  map: MysteryRoomMapDefinition,
  selections: MysteryRoomEditorSelection[],
): MysteryRoomRect | null {
  const bounds = uniqueEditorSelections(selections)
    .map((selection) => getEditorSelectionBounds(map, selection))
    .filter((rect): rect is MysteryRoomRect => Boolean(rect));
  if (bounds.length === 0) return null;
  const minX = Math.min(...bounds.map((rect) => rect.x));
  const minY = Math.min(...bounds.map((rect) => rect.y));
  const maxX = Math.max(...bounds.map((rect) => rect.x + rect.width));
  const maxY = Math.max(...bounds.map((rect) => rect.y + rect.height));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function editorWallPointKey(point: MysteryRoomVec2): string {
  return `${Math.round(point.x)}:${Math.round(point.y)}`;
}

function editorWallPointsEqual(left: MysteryRoomVec2, right: MysteryRoomVec2, tolerance = 0.5): boolean {
  return distanceBetween(left, right) <= tolerance;
}

function editorWallSegmentProjection(point: MysteryRoomVec2, wall: MysteryRoomSegment): { point: MysteryRoomVec2; distance: number; t: number } {
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared <= 0.0001) {
    return { point: { ...wall.start }, distance: distanceBetween(point, wall.start), t: 0 };
  }
  const rawT = ((point.x - wall.start.x) * dx + (point.y - wall.start.y) * dy) / lengthSquared;
  const t = Math.max(0, Math.min(1, rawT));
  const projected = { x: wall.start.x + dx * t, y: wall.start.y + dy * t };
  return { point: projected, distance: distanceBetween(point, projected), t };
}

function cloneWallSegmentStyle(wall: MysteryRoomSegment | undefined): Omit<MysteryRoomSegment, 'start' | 'end'> {
  if (!wall) {
    return { layer: defaultEditorLayerForSelectionType('wall') };
  }
  const { start: _start, end: _end, ...style } = wall;
  return { ...style, layer: normalizeEditorLayer(style.layer, defaultEditorLayerForSelectionType('wall')) };
}

export function getEditorWallNodes(sourceMap: MysteryRoomMapDefinition): MysteryRoomVec2[] {
  const map = normalizeEditableMap(sourceMap);
  const nodes = new Map<string, MysteryRoomVec2>();
  map.walls.forEach((wall) => {
    const start = roundPoint(wall.start);
    const end = roundPoint(wall.end);
    nodes.set(editorWallPointKey(start), start);
    nodes.set(editorWallPointKey(end), end);
  });
  return Array.from(nodes.values());
}

export function findEditorWallNode(
  sourceMap: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
  tolerance = MR_EDITOR_WALL_NODE_SNAP_RADIUS,
): MysteryRoomVec2 | null {
  let best: { node: MysteryRoomVec2; distance: number } | null = null;
  getEditorWallNodes(sourceMap).forEach((node) => {
    const distance = distanceBetween(point, node);
    if (distance > tolerance) return;
    if (!best || distance < best.distance) {
      best = { node, distance };
    }
  });
  return best ? { ...best.node } : null;
}

export function findEditorWallSegment(
  sourceMap: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
  tolerance = MR_EDITOR_WALL_SEGMENT_SNAP_RADIUS,
): { wallIndex: number; point: MysteryRoomVec2; distance: number } | null {
  const map = normalizeEditableMap(sourceMap);
  let best: { wallIndex: number; point: MysteryRoomVec2; distance: number; t: number } | null = null;
  map.walls.forEach((wall, wallIndex) => {
    const projection = editorWallSegmentProjection(point, wall);
    if (projection.t <= 0.02 || projection.t >= 0.98 || projection.distance > tolerance) return;
    if (!best || projection.distance < best.distance) {
      best = { wallIndex, point: roundPoint(projection.point), distance: projection.distance, t: projection.t };
    }
  });
  return best ? { wallIndex: best.wallIndex, point: best.point, distance: best.distance } : null;
}

export function insertEditorWallNodeAtPoint(
  sourceMap: MysteryRoomMapDefinition,
  wallIndex: number,
  point: MysteryRoomVec2,
): { map: MysteryRoomMapDefinition; node: MysteryRoomVec2; wallIndices: number[] } {
  const map = normalizeEditableMap(sourceMap);
  const wall = map.walls[wallIndex];
  const node = roundPoint(point);
  if (!wall) {
    return { map, node, wallIndices: [] };
  }
  if (editorWallPointsEqual(wall.start, node)) {
    return { map, node: roundPoint(wall.start), wallIndices: [wallIndex] };
  }
  if (editorWallPointsEqual(wall.end, node)) {
    return { map, node: roundPoint(wall.end), wallIndices: [wallIndex] };
  }
  const style = cloneWallSegmentStyle(wall);
  const nextWalls = [...map.walls];
  nextWalls.splice(
    wallIndex,
    1,
    { ...style, start: roundPoint(wall.start), end: node },
    { ...style, start: node, end: roundPoint(wall.end) },
  );
  return {
    map: normalizeEditableMap({ ...map, walls: nextWalls }),
    node,
    wallIndices: [wallIndex, wallIndex + 1],
  };
}

export function resolveEditorWallBuildPoint(
  sourceMap: MysteryRoomMapDefinition,
  point: MysteryRoomVec2,
): { map: MysteryRoomMapDefinition; point: MysteryRoomVec2; sourceWallIndex: number | null } {
  const map = normalizeEditableMap(sourceMap);
  const node = findEditorWallNode(map, point);
  if (node) {
    return { map, point: node, sourceWallIndex: null };
  }
  const segment = findEditorWallSegment(map, point);
  if (segment) {
    const inserted = insertEditorWallNodeAtPoint(map, segment.wallIndex, segment.point);
    return { map: inserted.map, point: inserted.node, sourceWallIndex: segment.wallIndex };
  }
  return { map, point: roundPoint(point), sourceWallIndex: null };
}

export function connectEditorWallPoints(
  sourceMap: MysteryRoomMapDefinition,
  start: MysteryRoomVec2,
  end: MysteryRoomVec2,
  styleWallIndex: number | null = null,
): { map: MysteryRoomMapDefinition; wallIndex: number | null; created: boolean } {
  const map = normalizeEditableMap(sourceMap);
  const startPoint = clampPointToMap(start, map);
  const endPoint = clampPointToMap(end, map);
  if (editorWallPointsEqual(startPoint, endPoint)) {
    return { map, wallIndex: null, created: false };
  }
  const existingIndex = map.walls.findIndex(
    (wall) =>
      (editorWallPointsEqual(wall.start, startPoint) && editorWallPointsEqual(wall.end, endPoint)) ||
      (editorWallPointsEqual(wall.start, endPoint) && editorWallPointsEqual(wall.end, startPoint)),
  );
  if (existingIndex >= 0) {
    return { map, wallIndex: existingIndex, created: false };
  }
  const style = cloneWallSegmentStyle(styleWallIndex === null ? undefined : map.walls[styleWallIndex]);
  const wallIndex = map.walls.length;
  return {
    map: normalizeEditableMap({
      ...map,
      walls: [...map.walls, { ...style, start: startPoint, end: endPoint }],
    }),
    wallIndex,
    created: true,
  };
}

export function moveEditorWallNode(
  sourceMap: MysteryRoomMapDefinition,
  node: MysteryRoomVec2,
  nextPoint: MysteryRoomVec2,
): MysteryRoomMapDefinition {
  const map = normalizeEditableMap(sourceMap);
  const target = clampPointToMap(nextPoint, map);
  return normalizeEditableMap({
    ...map,
    walls: map.walls.map((wall) => ({
      ...wall,
      start: editorWallPointsEqual(wall.start, node) ? target : wall.start,
      end: editorWallPointsEqual(wall.end, node) ? target : wall.end,
    })),
  });
}

export function getConnectedEditorWallIndices(sourceMap: MysteryRoomMapDefinition, wallIndex: number): number[] {
  const map = normalizeEditableMap(sourceMap);
  if (!map.walls[wallIndex]) return [];
  const visited = new Set<number>();
  const queue = [wallIndex];
  while (queue.length > 0) {
    const index = queue.shift()!;
    if (visited.has(index)) continue;
    const wall = map.walls[index];
    if (!wall) continue;
    visited.add(index);
    map.walls.forEach((candidate, candidateIndex) => {
      if (visited.has(candidateIndex)) return;
      const sharesNode =
        editorWallPointsEqual(wall.start, candidate.start) ||
        editorWallPointsEqual(wall.start, candidate.end) ||
        editorWallPointsEqual(wall.end, candidate.start) ||
        editorWallPointsEqual(wall.end, candidate.end);
      if (sharesNode) queue.push(candidateIndex);
    });
  }
  return Array.from(visited).sort((left, right) => left - right);
}

function editorEntityNamePrefix(tool: MysteryRoomEditorTool): string {
  switch (tool) {
    case 'switch':
      return 'switch';
    case 'sound':
      return 'sound';
    case 'nav':
      return 'nav';
    case 'spawn':
      return 'spawn';
    case 'bot':
      return 'bot';
    case 'boss':
      return 'boss';
    case 'access_card':
      return 'card';
    case 'quest_item':
      return 'item';
    case 'resource_bush':
      return 'bush';
    case 'resource_scrap':
      return 'scrap';
    case 'resource_rock':
      return 'rock';
    default:
      return tool;
  }
}

export function createEditorEntity(
  sourceMap: MysteryRoomMapDefinition,
  tool: MysteryRoomEditorTool,
  point: MysteryRoomVec2,
): MysteryRoomEditorCreateResult | null {
  const map = normalizeEditableMap(sourceMap);
  const id = nextEditorEntityName(map, editorEntityNamePrefix(tool));
  const zoneId = resolveZoneIdAtPoint(map, point);

  if (tool === 'room') {
    const rect = clampRectToMap({ x: point.x - 180, y: point.y - 120, width: 360, height: 240 }, map);
    return {
      map: {
        ...map,
        rooms: [
          ...map.rooms,
          {
            id,
            title: id,
            accessLevel: 'public',
            floor: [rect],
            color: 0x334155,
            labelPoint: rectCenter(rect),
          },
        ],
      },
      selection: { type: 'room', id },
    };
  }

  if (tool === 'wall') {
    return {
      map: {
        ...map,
        walls: [
          ...map.walls,
          {
            start: { x: Math.round(point.x - 130), y: Math.round(point.y) },
            end: { x: Math.round(point.x + 130), y: Math.round(point.y) },
            layer: defaultEditorLayerForSelectionType('wall'),
          },
        ],
      },
      selection: { type: 'wall', id: String(map.walls.length) },
    };
  }

  if (tool === 'door') {
    const panel = clampRectToMap({ x: point.x - 60, y: point.y - 13, width: 120, height: 26 }, map);
    return {
      map: {
        ...map,
        doors: [
          ...map.doors,
          {
            id,
            label: id,
            fromZoneId: zoneId,
            toZoneId: zoneId,
            requiredAccess: 'public',
            accessMode: 'public',
            accessCardObjectIds: [],
            panel,
            trigger: clampRectToMap({ x: point.x - 100, y: point.y - 70, width: 200, height: 140 }, map),
            orientation: 'horizontal',
            layer: defaultEditorLayerForSelectionType('door'),
          },
        ],
      },
      selection: { type: 'door', id },
    };
  }

  if (tool === 'light') {
    return {
      map: {
        ...map,
        lights: [
          ...map.lights,
          {
            id,
            label: id,
            zoneId,
            position: roundPoint(point),
            radius: 420,
            color: 0xfef3c7,
            colorTemperatureK: 4200,
            intensity: 0.85,
            flickerMode: 'steady',
            flickerIntensity: 0.26,
            flickerMinIntervalMs: 90,
            flickerMaxIntervalMs: 220,
            layer: defaultEditorLayerForSelectionType('light'),
          },
        ],
      },
      selection: { type: 'light', id },
    };
  }

  if (tool === 'switch') {
    const targetLight = map.lights.find((light) => light.zoneId === zoneId) ?? map.lights[0];
    const interactable: MysteryRoomInteractableDefinition = {
      id,
      kind: 'light_switch',
      zoneId,
      anchor: roundPoint(point),
      interactionRadius: 110,
      label: id,
      layer: defaultEditorLayerForSelectionType('interactable'),
      lightReaction: 'toggle',
      defaultEnabled: true,
    };
    return {
      map: {
        ...map,
        interactables: [...map.interactables, interactable],
        lights: targetLight
          ? map.lights.map((light) =>
              light.id === targetLight.id
                ? { ...light, enabled: interactable.defaultEnabled, switchId: id, switchIds: Array.from(new Set([...(light.switchIds ?? []), id])) }
                : light,
            )
          : map.lights,
      },
      selection: { type: 'interactable', id },
    };
  }

  if (tool === 'trigger') {
    const targetLight = map.lights.find((light) => light.zoneId === zoneId) ?? map.lights[0];
    const actions: MysteryRoomTriggerAction[] = targetLight ? [{ kind: 'toggle_light', lightIds: [targetLight.id] }] : [];
    return {
      map: {
        ...map,
        triggers: [
          ...map.triggers,
          {
            id,
            label: id,
            zoneId,
            rect: clampRectToMap({ x: point.x - 80, y: point.y - 50, width: 160, height: 100 }, map),
            event: 'onEnter',
            repeatable: true,
            cooldownMs: 1500,
            layer: defaultEditorLayerForSelectionType('trigger'),
            actions,
          },
        ],
      },
      selection: { type: 'trigger', id },
    };
  }

  if (tool === 'sound') {
    const soundId = `${id}_clip`;
    return {
      map: {
        ...map,
        soundDefinitions: [
          ...map.soundDefinitions,
          {
            id: soundId,
            label: soundId,
            kind: 'custom',
          },
        ],
        soundSources: [
          ...map.soundSources,
          {
            id,
            label: id,
            zoneId,
            position: roundPoint(point),
            soundId,
            radius: 360,
            volume: 0.55,
            loop: false,
            uniformMapVolume: false,
            uniformVolumeInRadius: false,
            layer: defaultEditorLayerForSelectionType('soundSource'),
          },
        ],
      },
      selection: { type: 'soundSource', id },
    };
  }

  if (tool === 'hazard') {
    const hazard: MysteryRoomHazardDefinition = {
      id,
      kind: 'smoke',
      zoneId,
      position: roundPoint(point),
      radius: 180,
      intensity: 0.72,
    };
    return {
      map: {
        ...map,
        hazards: [...map.hazards, hazard],
      },
      selection: { type: 'hazard', id },
    };
  }

  if (tool === 'incident') {
    const incidentAnchor: MysteryRoomIncidentAnchorDefinition = {
      id,
      kind: 'server_fault',
      zoneId,
      position: roundPoint(point),
    };
    return {
      map: {
        ...map,
        incidentAnchors: [...map.incidentAnchors, incidentAnchor],
      },
      selection: { type: 'incidentAnchor', id },
    };
  }

  if (tool === 'work') {
    const workAnchor: MysteryRoomWorkAnchorDefinition = {
      id,
      zoneId,
      kind: 'workstation',
      position: roundPoint(point),
      taskAreaShape: 'circle',
      taskAreaRadius: 84,
      allowedRoles: ['scientist'],
      description: 'Бот выполняет рабочую рутину и перемещается рядом с точкой.',
    };
    return {
      map: {
        ...map,
        workAnchors: [...map.workAnchors, workAnchor],
      },
      selection: { type: 'workAnchor', id },
    };
  }

  if (tool === 'spawn') {
    const slots = normalizeEditorSpawnSlots(map);
    const nextIndex = slots.length + 1;
    const spawnSlots = [
      ...slots,
      {
        x: Math.round(point.x),
        y: Math.round(point.y),
        room: zoneId,
        zoneId,
        label: `Игрок ${nextIndex}`,
        actor: 'player' as const,
        side: nextIndex <= 5 ? 'A' as const : 'B' as const,
      },
    ];
    return {
      map: withEditorSpawnSlots(map, spawnSlots),
      selection: { type: 'spawn', id: String(spawnSlots.length - 1) },
    };
  }

  if (tool === 'bot' || tool === 'boss') {
    const isBoss = tool === 'boss';
    return {
      map: {
        ...map,
        npcs: [
          ...map.npcs,
          {
            id,
            name: isBoss ? 'Босс' : 'Бот',
            role: isBoss ? 'guard' : 'scientist',
            actorKind: isBoss ? 'boss' : 'bot',
            actorPreset: isBoss ? 'boss' : 'npc',
            spawn: roundPoint(point),
            homeZoneId: zoneId,
            routeId: undefined,
            workAnchorIds: [],
            evacuationNodeId: map.navNodes.find((node) => node.kind === 'evac')?.id ?? map.navNodes[0]?.id ?? '',
            dutyActivation: 'auto',
            dutyTriggerIds: [],
            dutyDoorIds: [],
            accessLevels: isBoss ? ['public', 'lab', 'server', 'reactor'] : ['public'],
            color: isBoss ? 0xef4444 : 0xa5b4fc,
            radius: isBoss ? 34 : 16,
            health: isBoss ? 500 : 100,
            maxHealth: isBoss ? 500 : 100,
            speed: isBoss ? 88 : 118,
            visionRange: isBoss ? 720 : 560,
            visionHalfAngleDeg: isBoss ? 62 : 42,
            scale: isBoss ? 1.9 : 1,
            invisible: false,
          },
        ],
      },
      selection: { type: 'npc', id },
    };
  }

  if (tool === 'nav') {
    return {
      map: {
        ...map,
        navNodes: [
          ...map.navNodes,
          {
            id,
            zoneId,
            position: roundPoint(point),
            kind: 'hub',
          },
        ],
      },
      selection: { type: 'navNode', id },
    };
  }

  if (isEditorResourceTool(tool)) {
    const object = createEditorResourceObject(map, EDITOR_RESOURCE_TOOL_SOURCE_KIND[tool], point, id);
    return {
      map: {
        ...map,
        customObjects: [...map.customObjects, object],
      },
      selection: { type: 'customObject', id },
    };
  }

  if (tool === 'flashlight' || tool === 'access_card' || tool === 'pistol' || tool === 'quest_item') {
    const config = itemToolConfig(tool);
    const rect = clampRectToMap(
      {
        x: point.x - config.width / 2,
        y: point.y - config.height / 2,
        width: config.width,
        height: config.height,
      },
      map,
    );
    const object: MysteryRoomCustomObjectDefinition = {
      id,
      kind: config.kind,
      zoneId,
      rect,
      rotation: 0,
      color: config.color,
      shape: tool === 'pistol' || tool === 'flashlight' ? { kind: 'rect' } : createRegularCustomObjectShape(4),
      layer: MR_EDITOR_DEFAULT_LAYERS.item,
      accentColor: config.accentColor,
      label: id,
      itemId: config.itemId === 'self' ? id : config.itemId,
      pickupKind: config.pickupKind,
      inventoryIcon: config.inventoryIcon,
      ...(tool === 'access_card'
        ? {
            accessCardLevel: 'L2' as const,
            grantsAccess: getMysteryRoomAccessLevelForCardLevel('L2'),
          }
        : {}),
      ...(tool === 'pistol'
        ? {
            assetUrl: MYSTERY_ROOM_GLOCK19_ASSETS.worldUrl,
            assetName: `${MYSTERY_ROOM_GLOCK19_ASSETS.inventoryName} world`,
            assetMimeType: MYSTERY_ROOM_GLOCK19_ASSETS.mimeType,
            assetFit: 'contain' as const,
            assetTransform: defaultEditorObjectAssetTransform(),
            assetAlphaCollisionEnabled: true,
            colorEnabled: false,
            colorOpacity: 0,
            outlineEnabled: false,
            outlineOpacity: 0,
            outlineWidth: 0,
            inventoryIconAssetUrl: MYSTERY_ROOM_GLOCK19_ASSETS.inventoryUrl,
            inventoryIconAssetName: MYSTERY_ROOM_GLOCK19_ASSETS.inventoryName,
            inventoryIconAssetMimeType: MYSTERY_ROOM_GLOCK19_ASSETS.mimeType,
          }
        : tool === 'flashlight'
          ? {
              assetUrl: MYSTERY_ROOM_FLASHLIGHT_ASSETS.worldUrl,
              assetName: `${MYSTERY_ROOM_FLASHLIGHT_ASSETS.inventoryName} world`,
              assetMimeType: MYSTERY_ROOM_FLASHLIGHT_ASSETS.mimeType,
              assetFit: 'contain' as const,
              assetTransform: defaultEditorObjectAssetTransform(),
              assetAlphaCollisionEnabled: true,
              colorEnabled: false,
              colorOpacity: 0,
              outlineEnabled: false,
              outlineOpacity: 0,
              outlineWidth: 0,
              inventoryIconAssetUrl: MYSTERY_ROOM_FLASHLIGHT_ASSETS.inventoryUrl,
            inventoryIconAssetName: MYSTERY_ROOM_FLASHLIGHT_ASSETS.inventoryName,
            inventoryIconAssetMimeType: MYSTERY_ROOM_FLASHLIGHT_ASSETS.mimeType,
          }
        : tool === 'access_card'
          ? {
              assetUrl: MYSTERY_ROOM_DEFAULT_ACCESS_CARD_ASSET.worldUrl,
              assetName: `${MYSTERY_ROOM_DEFAULT_ACCESS_CARD_ASSET.inventoryName} world`,
              assetMimeType: MYSTERY_ROOM_DEFAULT_ACCESS_CARD_ASSET.mimeType,
              assetFit: 'contain' as const,
              assetTransform: defaultEditorObjectAssetTransform(),
              assetAlphaCollisionEnabled: true,
              colorEnabled: false,
              colorOpacity: 0,
              outlineEnabled: false,
              outlineOpacity: 0,
              outlineWidth: 0,
              inventoryIconAssetUrl: MYSTERY_ROOM_DEFAULT_ACCESS_CARD_ASSET.inventoryUrl,
              inventoryIconAssetName: MYSTERY_ROOM_DEFAULT_ACCESS_CARD_ASSET.inventoryName,
              inventoryIconAssetMimeType: MYSTERY_ROOM_DEFAULT_ACCESS_CARD_ASSET.mimeType,
            }
        : {}),
      collision: { kind: 'none' },
      collisionEnabled: false,
      physicsEnabled: false,
      weightKg: config.weightKg,
      blocksSight: false,
      visibleThroughWalls: false,
      interactive: true,
      tags: config.tags,
    };
    return {
      map: {
        ...map,
        customObjects: [...map.customObjects, object],
      },
      selection: { type: 'customObject', id },
    };
  }

  if (tool === 'table' || tool === 'object' || tool === 'collision') {
    const isCollision = tool === 'collision';
    const rect = clampRectToMap(
      {
        x: point.x - MR_EDITOR_DEFAULT_OBJECT_WIDTH / 2,
        y: point.y - MR_EDITOR_DEFAULT_OBJECT_HEIGHT / 2,
        width: MR_EDITOR_DEFAULT_OBJECT_WIDTH,
        height: MR_EDITOR_DEFAULT_OBJECT_HEIGHT,
      },
      map,
    );
    const object: MysteryRoomCustomObjectDefinition = {
      id,
      kind: tool === 'table' ? 'table' : 'primitive',
      zoneId,
      rect,
      rotation: 0,
      color: isCollision ? 0xef4444 : tool === 'table' ? 0x475569 : 0x38bdf8,
      shape: createRegularCustomObjectShape(4),
      layer: defaultEditorLayerForCustomObjectKind(tool === 'table' ? 'table' : 'primitive'),
      accentColor: isCollision ? 0xfca5a5 : 0xf8fafc,
      label: id,
      collision: isCollision
        ? { kind: 'rect', rect: { ...rect }, blocksMovement: true, blocksSight: false }
        : { kind: 'rect', rect: { ...rect }, blocksMovement: tool === 'table', blocksSight: false },
      collisionEnabled: isCollision || tool === 'table',
      physicsEnabled: false,
      weightKg: defaultEditorWeightForCustomObjectKind(tool === 'table' ? 'table' : isCollision ? 'wall' : 'primitive'),
      blocksSight: false,
      visibleThroughWalls: false,
      interactive: false,
      tags: isCollision ? ['collision'] : [],
    };
    const syncedObject = object.collisionEnabled ? withSyncedObjectCollision(object, rect) : object;
    return {
      map: {
        ...map,
        customObjects: [...map.customObjects, syncedObject],
      },
      selection: { type: 'customObject', id },
    };
  }

  return null;
}

export function createEditorResourceObject(
  sourceMap: MysteryRoomMapDefinition,
  sourceKind: MysteryRoomResourceSourceKind,
  point: MysteryRoomVec2,
  id = nextEditorEntityName(normalizeEditableMap(sourceMap), sourceKind),
): MysteryRoomCustomObjectDefinition {
  const map = normalizeEditableMap(sourceMap);
  const source = MYSTERY_ROOM_RESOURCE_SOURCE_DEFINITIONS[sourceKind];
  const resource = MYSTERY_ROOM_RESOURCE_DEFINITIONS[source.resourceKind];
  const rect = clampRectToMap(
    {
      x: point.x - source.width / 2,
      y: point.y - source.height / 2,
      width: source.width,
      height: source.height,
    },
    map,
  );
  return {
    id,
    kind: 'resource',
    zoneId: resolveZoneIdAtPoint(map, rectCenter(rect)),
    rect,
    rotation: 0,
    color: source.color,
    colorEnabled: true,
    colorOpacity: 0.72,
    outlineColor: source.accentColor,
    outlineEnabled: true,
    outlineOpacity: 0.72,
    outlineWidth: 2,
    shape: createRegularCustomObjectShape(sourceKind === 'bush' ? 7 : 5),
    layer: MR_EDITOR_DEFAULT_LAYERS.item,
    accentColor: source.accentColor,
    label: source.label,
    itemId: mysteryRoomResourceItemId(source.resourceKind),
    pickupKind: 'resource',
    resourceKind: source.resourceKind,
    resourceAmount: source.amount,
    resourceSourceKind: sourceKind,
    inventoryIcon: resource.icon,
    collision: { kind: 'none' },
    collisionEnabled: false,
    physicsEnabled: false,
    weightKg: source.resourceKind === 'metal' ? 8 : source.resourceKind === 'stone' ? 7 : 3,
    blocksSight: false,
    visibleThroughWalls: false,
    interactive: true,
    tags: ['pickup', 'resource', sourceKind, source.resourceKind],
  };
}

function isEditorResourceTool(tool: MysteryRoomEditorTool): tool is MysteryRoomEditorResourceTool {
  return tool === 'resource_bush' || tool === 'resource_scrap' || tool === 'resource_rock';
}

export function generateEditorResourceObjects(
  sourceMap: MysteryRoomMapDefinition,
  options: MysteryRoomEditorResourceGenerationOptions,
): MysteryRoomEditorMultiCreateResult | null {
  const map = normalizeEditableMap(sourceMap);
  const plan: Array<{ sourceKind: MysteryRoomResourceSourceKind; count: number }> = [
    { sourceKind: 'bush', count: normalizedResourceGenerationCount(options.bushCount) },
    { sourceKind: 'scrap', count: normalizedResourceGenerationCount(options.scrapCount) },
    { sourceKind: 'rock', count: normalizedResourceGenerationCount(options.rockCount) },
  ];
  const total = plan.reduce((sum, entry) => sum + entry.count, 0);
  if (total <= 0 || map.rooms.length === 0) {
    return null;
  }

  const rng = seededEditorResourceRandom(options.seed || `${map.id}:${map.customObjects.length}`);
  const occupiedRects = editorResourceOccupiedRects(map);
  const selections: MysteryRoomEditorSelection[] = [];
  let workingMap = map;

  for (const entry of plan) {
    for (let index = 0; index < entry.count; index += 1) {
      const object = placeGeneratedResourceObject(workingMap, entry.sourceKind, rng, occupiedRects);
      if (!object) {
        continue;
      }
      occupiedRects.push(expandEditorRect(object.rect, 20));
      workingMap = {
        ...workingMap,
        customObjects: [...workingMap.customObjects, object],
      };
      selections.push({ type: 'customObject', id: object.id });
    }
  }

  return selections.length > 0
    ? {
        map: normalizeEditableMap(workingMap),
        selections,
      }
    : null;
}

function placeGeneratedResourceObject(
  map: MysteryRoomMapDefinition,
  sourceKind: MysteryRoomResourceSourceKind,
  rng: () => number,
  occupiedRects: MysteryRoomRect[],
): MysteryRoomCustomObjectDefinition | null {
  const source = MYSTERY_ROOM_RESOURCE_SOURCE_DEFINITIONS[sourceKind];
  const roomsWithSpace = map.rooms.filter((room) =>
    room.floor.some((floor) => floor.width >= source.width + 120 && floor.height >= source.height + 120),
  );
  if (roomsWithSpace.length === 0) {
    return null;
  }

  for (let attempt = 0; attempt < 120; attempt += 1) {
    const room = roomsWithSpace[Math.floor(rng() * roomsWithSpace.length)] ?? roomsWithSpace[0];
    const floors = room.floor.filter((floor) => floor.width >= source.width + 120 && floor.height >= source.height + 120);
    const floor = floors[Math.floor(rng() * floors.length)] ?? floors[0];
    const margin = 56;
    const point = {
      x: floor.x + margin + rng() * Math.max(1, floor.width - margin * 2),
      y: floor.y + margin + rng() * Math.max(1, floor.height - margin * 2),
    };
    const id = nextEditorEntityName(map, sourceKind);
    const object = createEditorResourceObject(map, sourceKind, point, id);
    if (room.id !== object.zoneId || !rectInsideEditorFloor(object.rect, floor)) {
      continue;
    }
    if (occupiedRects.some((rect) => rectsOverlap(object.rect, rect))) {
      continue;
    }
    return object;
  }

  return null;
}

function normalizedResourceGenerationCount(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return Math.min(80, Math.floor(value));
}

function seededEditorResourceRandom(seedText: string) {
  let seed = 2166136261;
  for (const char of seedText) {
    seed ^= char.charCodeAt(0);
    seed = Math.imul(seed, 16777619);
  }
  return () => {
    seed = Math.imul(seed ^ (seed >>> 15), 2246822507);
    seed = Math.imul(seed ^ (seed >>> 13), 3266489909);
    seed ^= seed >>> 16;
    return (seed >>> 0) / 4294967296;
  };
}

function editorResourceOccupiedRects(map: MysteryRoomMapDefinition): MysteryRoomRect[] {
  const spawnRects = normalizeEditorSpawnSlots(map).map((slot) => ({
    x: slot.x - 72,
    y: slot.y - 72,
    width: 144,
    height: 144,
  }));
  const doorRects = map.doors.flatMap((door) => [expandEditorRect(door.panel, 28), expandEditorRect(door.trigger, 18)]);
  const objectRects = map.customObjects.map((object) => expandEditorRect(object.rect, 22));
  const interactableRects = map.interactables.map((entry) => ({
    x: entry.anchor.x - 52,
    y: entry.anchor.y - 52,
    width: 104,
    height: 104,
  }));
  return [...spawnRects, ...doorRects, ...objectRects, ...interactableRects];
}

function expandEditorRect(rect: MysteryRoomRect, amount: number): MysteryRoomRect {
  return {
    x: rect.x - amount,
    y: rect.y - amount,
    width: rect.width + amount * 2,
    height: rect.height + amount * 2,
  };
}

function rectInsideEditorFloor(rect: MysteryRoomRect, floor: MysteryRoomRect): boolean {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    rectCenter(rect),
  ].every((point) => rectContainsPoint(floor, point));
}

function rectsOverlap(a: MysteryRoomRect, b: MysteryRoomRect): boolean {
  return a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y;
}

function itemToolConfig(tool: Extract<MysteryRoomEditorTool, 'flashlight' | 'access_card' | 'pistol' | 'quest_item'>) {
  switch (tool) {
    case 'flashlight':
      return {
        kind: 'equipment' as const,
        itemId: 'flashlight' as const,
        pickupKind: 'tool' as const,
        width: MR_EDITOR_DEFAULT_OBJECT_WIDTH,
        height: MR_EDITOR_DEFAULT_OBJECT_HEIGHT,
        color: 0xfacc15,
        accentColor: 0xfef3c7,
        weightKg: 1,
        inventoryIcon: 'FL',
        tags: ['pickup', 'flashlight'],
      };
    case 'access_card':
      return {
        kind: 'badge' as const,
        itemId: 'self' as const,
        pickupKind: 'access_card' as const,
        width: MR_EDITOR_DEFAULT_OBJECT_WIDTH,
        height: MR_EDITOR_DEFAULT_OBJECT_HEIGHT,
        color: 0x22d3ee,
        accentColor: 0xf8fafc,
        weightKg: 0.1,
        inventoryIcon: 'ID',
        tags: ['pickup', 'access_card'],
      };
    case 'pistol':
      return {
        kind: 'weapon' as const,
        itemId: 'pistol' as const,
        pickupKind: 'weapon' as const,
        width: MR_EDITOR_DEFAULT_OBJECT_WIDTH,
        height: MR_EDITOR_DEFAULT_OBJECT_HEIGHT,
        color: 0x111827,
        accentColor: 0xfacc15,
        weightKg: 1.4,
        inventoryIcon: 'PS',
        tags: ['pickup', 'weapon', 'pistol'],
      };
    case 'quest_item':
    default:
      return {
        kind: 'equipment' as const,
        itemId: 'self' as const,
        pickupKind: 'tool' as const,
        width: MR_EDITOR_DEFAULT_OBJECT_WIDTH,
        height: MR_EDITOR_DEFAULT_OBJECT_HEIGHT,
        color: 0xa78bfa,
        accentColor: 0xf5d0fe,
        weightKg: 2,
        inventoryIcon: 'QI',
        tags: ['pickup', 'quest_item'],
      };
  }
}

function defaultEditorObjectAssetTransform() {
  return {
    offsetX: 0,
    offsetY: 0,
    scaleX: (MR_EDITOR_DEFAULT_OBJECT_WIDTH - MR_EDITOR_DEFAULT_OBJECT_INNER_PADDING * 2) / MR_EDITOR_DEFAULT_OBJECT_WIDTH,
    scaleY: (MR_EDITOR_DEFAULT_OBJECT_HEIGHT - MR_EDITOR_DEFAULT_OBJECT_INNER_PADDING * 2) / MR_EDITOR_DEFAULT_OBJECT_HEIGHT,
    rotation: 0,
    clipToBounds: true,
  };
}

export function duplicateEditorSelection(
  sourceMap: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
  offset: MysteryRoomVec2 = { x: 160, y: 0 },
): MysteryRoomEditorCreateResult | null {
  if (!selection) {
    return null;
  }
  const map = normalizeEditableMap(sourceMap);
  const dx = Math.round(offset.x);
  const dy = Math.round(offset.y);

  switch (selection.type) {
    case 'spawn': {
      const source = spawnSlotForSelection(map, selection.id);
      if (!source) return null;
      const actor: MysteryRoomSpawnSlotDefinition['actor'] = source.actor === 'bot' ? 'bot' : 'player';
      const side: MysteryRoomSpawnSlotDefinition['side'] = source.side === 'B' ? 'B' : 'A';
      const spawnSlots = [
        ...normalizeEditorSpawnSlots(map),
        {
          x: Math.round(source.x + dx),
          y: Math.round(source.y + dy),
          room: source.room ?? source.zoneId ?? map.spawnZoneId,
          zoneId: source.room ?? source.zoneId ?? map.spawnZoneId,
          label: `${actor === 'bot' ? 'Бот' : 'Игрок'} ${normalizeEditorSpawnSlots(map).length + 1}`,
          actor,
          side,
        },
      ];
      return {
        map: withEditorSpawnSlots(map, spawnSlots),
        selection: { type: 'spawn', id: String(spawnSlots.length - 1) },
      };
    }
    case 'room': {
      const source = map.rooms.find((room) => room.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id || source.title, 'room');
      const room = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          rooms: [
            ...map.rooms,
            {
              ...room,
              id,
              title: id,
              floor: room.floor.map((rect) => offsetRect(rect, dx, dy)),
              labelPoint: { x: Math.round(room.labelPoint.x + dx), y: Math.round(room.labelPoint.y + dy) },
            },
          ],
        }),
        selection: { type: 'room', id },
      };
    }
    case 'wall': {
      const source = map.walls[Number(selection.id)];
      if (!source) return null;
      const wall = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          walls: [
            ...map.walls,
            {
              ...wall,
              start: { x: Math.round(wall.start.x + dx), y: Math.round(wall.start.y + dy) },
              end: { x: Math.round(wall.end.x + dx), y: Math.round(wall.end.y + dy) },
            },
          ],
        }),
        selection: { type: 'wall', id: String(map.walls.length) },
      };
    }
    case 'door': {
      const source = map.doors.find((door) => door.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id, 'door');
      const door = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          doors: [
            ...map.doors,
            {
              ...door,
              id,
              panel: offsetRect(door.panel, dx, dy),
              trigger: offsetRect(door.trigger, dx, dy),
            },
          ],
        }),
        selection: { type: 'door', id },
      };
    }
    case 'customObject': {
      const source = map.customObjects.find((object) => object.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id || source.label, 'object');
      const object = moveCustomObject({ ...deepClone(source), id, label: id }, dx, dy);
      return {
        map: normalizeEditableMap({
          ...map,
          customObjects: [...map.customObjects, object],
        }),
        selection: { type: 'customObject', id },
      };
    }
    case 'npc': {
      const source = map.npcs.find((npc) => npc.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id || source.name, source.actorKind === 'boss' ? 'boss' : 'bot');
      const npc = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          npcs: [
            ...map.npcs,
            {
              ...npc,
              id,
              name: id,
              spawn: { x: Math.round(npc.spawn.x + dx), y: Math.round(npc.spawn.y + dy) },
              homeZoneId: resolveZoneIdAtPoint(map, { x: Math.round(npc.spawn.x + dx), y: Math.round(npc.spawn.y + dy) }),
            },
          ],
        }),
        selection: { type: 'npc', id },
      };
    }
    case 'light': {
      const source = map.lights.find((light) => light.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id || source.label, 'light');
      const light = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          lights: [
            ...map.lights,
            {
              ...light,
              id,
              label: id,
              position: { x: Math.round(light.position.x + dx), y: Math.round(light.position.y + dy) },
            },
          ],
        }),
        selection: { type: 'light', id },
      };
    }
    case 'interactable': {
      const source = map.interactables.find((entry) => entry.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id || source.label, 'interactable');
      const interactable = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          interactables: [
            ...map.interactables,
            {
              ...interactable,
              id,
              label: id,
              anchor: { x: Math.round(interactable.anchor.x + dx), y: Math.round(interactable.anchor.y + dy) },
            },
          ],
        }),
        selection: { type: 'interactable', id },
      };
    }
    case 'trigger': {
      const source = map.triggers.find((trigger) => trigger.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id || source.label, 'trigger');
      const trigger = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          triggers: [
            ...map.triggers,
            {
              ...trigger,
              id,
              label: id,
              rect: offsetRect(trigger.rect, dx, dy),
            },
          ],
        }),
        selection: { type: 'trigger', id },
      };
    }
    case 'soundSource': {
      const source = map.soundSources.find((entry) => entry.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id || source.label, 'sound');
      const soundSource = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          soundSources: [
            ...map.soundSources,
            {
              ...soundSource,
              id,
              label: id,
              position: { x: Math.round(soundSource.position.x + dx), y: Math.round(soundSource.position.y + dy) },
            },
          ],
        }),
        selection: { type: 'soundSource', id },
      };
    }
    case 'hazard': {
      const source = map.hazards.find((hazard) => hazard.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id, 'hazard');
      const hazard = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          hazards: [
            ...map.hazards,
            {
              ...hazard,
              id,
              position: { x: Math.round(hazard.position.x + dx), y: Math.round(hazard.position.y + dy) },
            },
          ],
        }),
        selection: { type: 'hazard', id },
      };
    }
    case 'incidentAnchor': {
      const source = map.incidentAnchors.find((anchor) => anchor.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id, 'incident');
      const anchor = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          incidentAnchors: [
            ...map.incidentAnchors,
            {
              ...anchor,
              id,
              position: { x: Math.round(anchor.position.x + dx), y: Math.round(anchor.position.y + dy) },
            },
          ],
        }),
        selection: { type: 'incidentAnchor', id },
      };
    }
    case 'workAnchor': {
      const source = map.workAnchors.find((anchor) => anchor.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id, 'work');
      const anchor = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          workAnchors: [
            ...map.workAnchors,
            {
              ...anchor,
              id,
              position: { x: Math.round(anchor.position.x + dx), y: Math.round(anchor.position.y + dy) },
            },
          ],
        }),
        selection: { type: 'workAnchor', id },
      };
    }
    case 'navNode': {
      const source = map.navNodes.find((node) => node.id === selection.id);
      if (!source) return null;
      const id = nextDuplicatedEditorEntityName(map, source.id, 'nav');
      const node = deepClone(source);
      return {
        map: normalizeEditableMap({
          ...map,
          navNodes: [
            ...map.navNodes,
            {
              ...node,
              id,
              position: { x: Math.round(node.position.x + dx), y: Math.round(node.position.y + dy) },
            },
          ],
        }),
        selection: { type: 'navNode', id },
      };
    }
    default:
      return null;
  }
}

export function duplicateEditorSelections(
  sourceMap: MysteryRoomMapDefinition,
  selections: MysteryRoomEditorSelection[],
  options?: { targetPoint?: MysteryRoomVec2 | null; offset?: MysteryRoomVec2 },
): MysteryRoomEditorMultiCreateResult | null {
  const copyableSelections = uniqueEditorSelections(selections);
  if (copyableSelections.length === 0) return null;
  const map = normalizeEditableMap(sourceMap);
  const bounds = options?.targetPoint ? getEditorSelectionsBounds(map, copyableSelections) : null;
  const offset = bounds && options?.targetPoint
    ? {
        x: Math.round(options.targetPoint.x - (bounds.x + bounds.width / 2)),
        y: Math.round(options.targetPoint.y - (bounds.y + bounds.height / 2)),
      }
    : options?.offset ?? { x: 160, y: 0 };
  let nextMap = map;
  const nextSelections: MysteryRoomEditorSelection[] = [];
  copyableSelections.forEach((selection) => {
    const result = duplicateEditorSelection(nextMap, selection, offset);
    if (!result) return;
    nextMap = result.map;
    nextSelections.push(result.selection);
  });
  if (nextSelections.length === 0) return null;
  return { map: normalizeEditableMap(nextMap), selections: nextSelections };
}

export function moveEditorSelection(
  map: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
  delta: MysteryRoomVec2,
): MysteryRoomMapDefinition {
  if (!selection || (delta.x === 0 && delta.y === 0)) {
    return map;
  }
  const next = normalizeEditableMap(map);
  const dx = Math.round(delta.x);
  const dy = Math.round(delta.y);

  switch (selection.type) {
    case 'room':
      return {
        ...next,
        rooms: next.rooms.map((room) =>
          room.id === selection.id
            ? {
                ...room,
                floor: room.floor.map((rect) => clampRectToMap(offsetRect(rect, dx, dy), next)),
                labelPoint: { x: room.labelPoint.x + dx, y: room.labelPoint.y + dy },
              }
            : room,
        ),
      };
    case 'wall': {
      const index = Number(selection.id);
      return {
        ...next,
        walls: next.walls.map((wall, wallIndex) =>
          wallIndex === index
            ? {
                ...wall,
                start: { x: wall.start.x + dx, y: wall.start.y + dy },
                end: { x: wall.end.x + dx, y: wall.end.y + dy },
              }
            : wall,
        ),
      };
    }
    case 'door':
      return {
        ...next,
        doors: next.doors.map((door) =>
          door.id === selection.id
            ? { ...door, panel: offsetRect(door.panel, dx, dy), trigger: offsetRect(door.trigger, dx, dy) }
            : door,
        ),
      };
    case 'customObject':
      return {
        ...next,
        customObjects: next.customObjects.map((object) =>
          object.id === selection.id ? moveCustomObject(object, dx, dy) : object,
        ),
      };
    case 'npc':
      return {
        ...next,
        npcs: next.npcs.map((npc) =>
          npc.id === selection.id
            ? {
                ...npc,
                spawn: { x: npc.spawn.x + dx, y: npc.spawn.y + dy },
                homeZoneId: resolveZoneIdAtPoint(next, { x: npc.spawn.x + dx, y: npc.spawn.y + dy }),
              }
            : npc,
        ),
      };
    case 'light':
      return {
        ...next,
        lights: next.lights.map((light) =>
          light.id === selection.id ? { ...light, position: { x: light.position.x + dx, y: light.position.y + dy } } : light,
        ),
      };
    case 'interactable':
      return {
        ...next,
        interactables: next.interactables.map((entry) =>
          entry.id === selection.id ? { ...entry, anchor: { x: entry.anchor.x + dx, y: entry.anchor.y + dy } } : entry,
        ),
      };
    case 'trigger':
      return {
        ...next,
        triggers: next.triggers.map((trigger) =>
          trigger.id === selection.id ? { ...trigger, rect: offsetRect(trigger.rect, dx, dy) } : trigger,
        ),
      };
    case 'hazard':
      return {
        ...next,
        hazards: next.hazards.map((hazard) =>
          hazard.id === selection.id ? { ...hazard, position: { x: hazard.position.x + dx, y: hazard.position.y + dy } } : hazard,
        ),
      };
    case 'incidentAnchor':
      return {
        ...next,
        incidentAnchors: next.incidentAnchors.map((anchor) =>
          anchor.id === selection.id ? { ...anchor, position: { x: anchor.position.x + dx, y: anchor.position.y + dy } } : anchor,
        ),
      };
    case 'workAnchor':
      return {
        ...next,
        workAnchors: next.workAnchors.map((anchor) =>
          anchor.id === selection.id ? { ...anchor, position: { x: anchor.position.x + dx, y: anchor.position.y + dy } } : anchor,
        ),
      };
    case 'soundSource':
      return {
        ...next,
        soundSources: next.soundSources.map((source) =>
          source.id === selection.id ? { ...source, position: { x: source.position.x + dx, y: source.position.y + dy } } : source,
        ),
      };
    case 'navNode':
      return {
        ...next,
        navNodes: next.navNodes.map((node) =>
          node.id === selection.id ? { ...node, position: { x: node.position.x + dx, y: node.position.y + dy } } : node,
        ),
      };
    case 'spawn':
      return withEditorSpawnSlots(
        next,
        normalizeEditorSpawnSlots(next).map((slot, index) => {
          if (index !== spawnSlotIndexFromSelectionId(selection.id)) return slot;
          const point = { x: slot.x + dx, y: slot.y + dy };
          const zoneId = resolveZoneIdAtPoint(next, point);
          return { ...slot, x: point.x, y: point.y, room: zoneId, zoneId };
        }),
      );
    default:
      return next;
  }
}

export function moveEditorSelections(
  map: MysteryRoomMapDefinition,
  selections: MysteryRoomEditorSelection[],
  delta: MysteryRoomVec2,
): MysteryRoomMapDefinition {
  return uniqueEditorSelections(selections).reduce(
    (current, selection) => moveEditorSelection(current, selection, delta),
    map,
  );
}

export function deleteEditorSelection(
  map: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
): MysteryRoomMapDefinition {
  if (!selection) {
    return map;
  }
  const next = normalizeEditableMap(map);
  switch (selection.type) {
    case 'spawn': {
      const slots = normalizeEditorSpawnSlots(next);
      if (slots.length <= 1) return next;
      return withEditorSpawnSlots(next, slots.filter((_slot, index) => index !== spawnSlotIndexFromSelectionId(selection.id)));
    }
    case 'room':
      return { ...next, rooms: next.rooms.filter((room) => room.id !== selection.id) };
    case 'wall':
      return { ...next, walls: next.walls.filter((_wall, index) => String(index) !== selection.id) };
    case 'door':
      return { ...next, doors: next.doors.filter((door) => door.id !== selection.id) };
    case 'customObject':
      return { ...next, customObjects: next.customObjects.filter((object) => object.id !== selection.id) };
    case 'npc':
      return { ...next, npcs: next.npcs.filter((npc) => npc.id !== selection.id) };
    case 'light':
      return { ...next, lights: next.lights.filter((light) => light.id !== selection.id) };
    case 'interactable':
      return { ...next, interactables: next.interactables.filter((entry) => entry.id !== selection.id) };
    case 'trigger':
      return { ...next, triggers: next.triggers.filter((trigger) => trigger.id !== selection.id) };
    case 'hazard':
      return { ...next, hazards: next.hazards.filter((hazard) => hazard.id !== selection.id) };
    case 'incidentAnchor':
      return { ...next, incidentAnchors: next.incidentAnchors.filter((anchor) => anchor.id !== selection.id) };
    case 'workAnchor':
      return { ...next, workAnchors: next.workAnchors.filter((anchor) => anchor.id !== selection.id) };
    case 'soundSource':
      return { ...next, soundSources: next.soundSources.filter((source) => source.id !== selection.id) };
    case 'navNode':
      return { ...next, navNodes: next.navNodes.filter((node) => node.id !== selection.id) };
    default:
      return next;
  }
}

export function getEditorSelectionLayer(
  map: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
): number | null {
  if (!selection || !isLayerableSelection(selection)) {
    return null;
  }
  const normalized = normalizeEditableMap(map);
  switch (selection.type) {
    case 'wall': {
      const wall = normalized.walls[Number(selection.id)];
      return wall ? normalizeEditorLayer(wall.layer, defaultEditorLayerForSelectionType('wall')) : null;
    }
    case 'door':
      return normalized.doors.find((entry) => entry.id === selection.id)?.layer ?? null;
    case 'customObject':
      return normalized.customObjects.find((entry) => entry.id === selection.id)?.layer ?? null;
    case 'light':
      return normalized.lights.find((entry) => entry.id === selection.id)?.layer ?? null;
    case 'interactable':
      return normalized.interactables.find((entry) => entry.id === selection.id)?.layer ?? null;
    case 'trigger':
      return normalized.triggers.find((entry) => entry.id === selection.id)?.layer ?? null;
    case 'soundSource':
      return normalized.soundSources.find((entry) => entry.id === selection.id)?.layer ?? null;
    default:
      return null;
  }
}

export function setEditorSelectionLayer(
  map: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
  layer: number,
): MysteryRoomMapDefinition {
  if (!selection || !isLayerableSelection(selection)) {
    return map;
  }
  const next = normalizeEditableMap(map);
  const normalizedLayer = normalizeEditorLayer(layer, defaultEditorLayerForSelectionType(selection.type));
  switch (selection.type) {
    case 'wall': {
      const index = Number(selection.id);
      return {
        ...next,
        walls: next.walls.map((wall, wallIndex) => (wallIndex === index ? { ...wall, layer: normalizedLayer } : wall)),
      };
    }
    case 'door':
      return { ...next, doors: next.doors.map((door) => (door.id === selection.id ? { ...door, layer: normalizedLayer } : door)) };
    case 'customObject':
      return { ...next, customObjects: next.customObjects.map((object) => (object.id === selection.id ? { ...object, layer: normalizedLayer } : object)) };
    case 'light':
      return { ...next, lights: next.lights.map((light) => (light.id === selection.id ? { ...light, layer: normalizedLayer } : light)) };
    case 'interactable':
      return { ...next, interactables: next.interactables.map((entry) => (entry.id === selection.id ? { ...entry, layer: normalizedLayer } : entry)) };
    case 'trigger':
      return { ...next, triggers: next.triggers.map((trigger) => (trigger.id === selection.id ? { ...trigger, layer: normalizedLayer } : trigger)) };
    case 'soundSource':
      return { ...next, soundSources: next.soundSources.map((source) => (source.id === selection.id ? { ...source, layer: normalizedLayer } : source)) };
    default:
      return next;
  }
}

export function adjustEditorSelectionLayer(
  map: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
  delta: number,
): MysteryRoomMapDefinition {
  const currentLayer = getEditorSelectionLayer(map, selection);
  if (currentLayer === null || !selection || !isLayerableSelection(selection)) {
    return map;
  }
  return setEditorSelectionLayer(map, selection, currentLayer + delta);
}

export function transformEditorSelection(
  map: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
  action: MysteryRoomEditorTransformAction,
): MysteryRoomMapDefinition {
  if (!selection) return map;
  const next = normalizeEditableMap(map);

  switch (selection.type) {
    case 'room':
      return {
        ...next,
        rooms: next.rooms.map((room) => {
          if (room.id !== selection.id || !room.floor[0]) return room;
          if (!isSizeAction(action)) return room;
          const rect = clampRectToMap(resizeRectByAction(room.floor[0], action, { minWidth: 160, minHeight: 120 }), next);
          return {
            ...room,
            floor: [rect, ...room.floor.slice(1)],
            labelPoint: { x: rect.x + rect.width / 2, y: rect.y + 22 },
          };
        }),
      };
    case 'wall': {
      const index = Number(selection.id);
      return {
        ...next,
        walls: next.walls.map((wall, wallIndex) => {
          if (wallIndex !== index) return wall;
          if (action === 'rotate_left' || action === 'rotate_right') {
            return rotateWallSegment(wall, action === 'rotate_right' ? 15 : -15, next);
          }
          if (action === 'length_decrease' || action === 'length_increase') {
            return resizeWallSegment(wall, action === 'length_increase' ? 48 : -48, next);
          }
          return wall;
        }),
      };
    }
    case 'door':
      return {
        ...next,
        doors: next.doors.map((door) => {
          if (door.id !== selection.id) return door;
          if (action === 'rotate_left' || action === 'rotate_right') {
            return rotateDoorPanel(door, next);
          }
          if (isSizeAction(action)) {
            return resizeDoorPanel(door, action, next);
          }
          return door;
        }),
      };
    case 'customObject':
      return {
        ...next,
        customObjects: next.customObjects.map((object) => {
          if (object.id !== selection.id) return object;
          if (action === 'rotate_left' || action === 'rotate_right') {
            return { ...object, rotation: normalizeDegrees(object.rotation + (action === 'rotate_right' ? 15 : -15)) };
          }
          if (isSizeAction(action)) {
            return resizeCustomObjectByAction(object, action, next);
          }
          return object;
        }),
      };
    case 'light':
      return {
        ...next,
        lights: next.lights.map((light) =>
          light.id === selection.id && isRadiusAction(action)
            ? { ...light, radius: clampNumber(light.radius + (action === 'radius_increase' ? 48 : -48), 80, 3000) }
            : light,
        ),
      };
    case 'interactable':
      return {
        ...next,
        interactables: next.interactables.map((entry) =>
          entry.id === selection.id && isRadiusAction(action)
            ? { ...entry, interactionRadius: clampNumber(entry.interactionRadius + (action === 'radius_increase' ? 24 : -24), 40, 1200) }
            : entry,
        ),
      };
    case 'trigger':
      return {
        ...next,
        triggers: next.triggers.map((trigger) =>
          trigger.id === selection.id && isSizeAction(action)
            ? { ...trigger, rect: clampRectToMap(resizeRectByAction(trigger.rect, action, { minWidth: 48, minHeight: 48 }), next) }
            : trigger,
        ),
      };
    case 'hazard':
      return {
        ...next,
        hazards: next.hazards.map((hazard) =>
          hazard.id === selection.id && isRadiusAction(action)
            ? { ...hazard, radius: clampNumber(hazard.radius + (action === 'radius_increase' ? 36 : -36), 40, 2400) }
            : hazard,
        ),
      };
    case 'soundSource':
      return {
        ...next,
        soundSources: next.soundSources.map((source) =>
          source.id === selection.id && isRadiusAction(action)
            ? { ...source, radius: clampNumber(source.radius + (action === 'radius_increase' ? 48 : -48), 60, 3000) }
            : source,
        ),
      };
    default:
      return next;
  }
}

export function dragTransformEditorSelection(
  map: MysteryRoomMapDefinition,
  selection: MysteryRoomEditorSelection | null,
  handle: MysteryRoomEditorTransformHandle,
  startPoint: MysteryRoomVec2,
  currentPoint: MysteryRoomVec2,
): MysteryRoomMapDefinition {
  if (!selection) return map;
  const next = normalizeEditableMap(map);

  switch (selection.type) {
    case 'room':
      if (handle !== 'length' && handle !== 'thickness' && !isRectSideHandle(handle)) return next;
      return {
        ...next,
        rooms: next.rooms.map((room) => {
          if (room.id !== selection.id || !room.floor[0]) return room;
          const rect = isRectSideHandle(handle)
            ? resizeAxisAlignedRectSideByDrag(room.floor[0], rectSideFromHandle(handle), startPoint, currentPoint, { minWidth: 160, minHeight: 120 }, next)
            : resizeAxisAlignedRectByDrag(room.floor[0], handle, startPoint, currentPoint, { minWidth: 160, minHeight: 120 }, next);
          return {
            ...room,
            floor: [rect, ...room.floor.slice(1)],
            labelPoint: { x: rect.x + rect.width / 2, y: rect.y + 22 },
          };
        }),
      };
    case 'wall': {
      const index = Number(selection.id);
      return {
        ...next,
        walls: next.walls.map((wall, wallIndex) => {
          if (wallIndex !== index) return wall;
          if (handle === 'rotate') {
            return rotateWallSegment(wall, angleDeltaAround(segmentCenter(wall), startPoint, currentPoint), next);
          }
          if (handle === 'length') {
            return resizeWallSegment(wall, 2 * projectDeltaOntoAxis(startPoint, currentPoint, wallAxis(wall)), next);
          }
          if (isWallEndpointHandle(handle)) {
            return dragWallEndpoint(wall, wallEndpointFromHandle(handle), startPoint, currentPoint, next);
          }
          return wall;
        }),
      };
    }
    case 'door':
      return {
        ...next,
        doors: next.doors.map((door) => {
          if (door.id !== selection.id) return door;
          if (handle === 'rotate') {
            return Math.abs(angleDeltaAround(rectCenter(door.panel), startPoint, currentPoint)) >= 45 ? rotateDoorPanel(door, next) : door;
          }
          if (handle === 'length' || handle === 'thickness') {
            return resizeDoorPanelByDrag(door, handle, startPoint, currentPoint, next);
          }
          if (isRectSideHandle(handle)) {
            return resizeDoorPanelSideByDrag(door, rectSideFromHandle(handle), startPoint, currentPoint, next);
          }
          return door;
        }),
      };
    case 'customObject':
      return {
        ...next,
        customObjects: next.customObjects.map((object) => {
          if (object.id !== selection.id) return object;
          if (handle === 'rotate') {
            return { ...object, rotation: normalizeDegrees(object.rotation + angleDeltaAround(rectCenter(object.rect), startPoint, currentPoint)) };
          }
          if (handle === 'length' || handle === 'thickness') {
            return resizeCustomObjectByDrag(object, handle, startPoint, currentPoint, next);
          }
          if (isCustomObjectEdgeHandle(handle)) {
            return dragCustomObjectShapeEdge(object, customObjectEdgeHandleIndex(handle), startPoint, currentPoint, next);
          }
          if (isRectSideHandle(handle)) {
            return resizeCustomObjectSideByDrag(object, rectSideFromHandle(handle), startPoint, currentPoint, next);
          }
          return object;
        }),
      };
    case 'light':
      return {
        ...next,
        lights: next.lights.map((light) =>
          light.id === selection.id && handle === 'radius'
            ? { ...light, radius: clampNumber(distanceBetween(light.position, currentPoint), 80, 3000) }
            : light,
        ),
      };
    case 'interactable':
      return {
        ...next,
        interactables: next.interactables.map((entry) =>
          entry.id === selection.id && handle === 'radius'
            ? { ...entry, interactionRadius: clampNumber(distanceBetween(entry.anchor, currentPoint), 40, 1200) }
            : entry,
        ),
      };
    case 'trigger':
      if (handle !== 'length' && handle !== 'thickness' && !isRectSideHandle(handle)) return next;
      return {
        ...next,
        triggers: next.triggers.map((trigger) =>
          trigger.id === selection.id
            ? {
                ...trigger,
                rect: isRectSideHandle(handle)
                  ? resizeAxisAlignedRectSideByDrag(trigger.rect, rectSideFromHandle(handle), startPoint, currentPoint, { minWidth: 48, minHeight: 48 }, next)
                  : resizeAxisAlignedRectByDrag(trigger.rect, handle, startPoint, currentPoint, { minWidth: 48, minHeight: 48 }, next),
              }
            : trigger,
        ),
      };
    case 'hazard':
      return {
        ...next,
        hazards: next.hazards.map((hazard) =>
          hazard.id === selection.id && handle === 'radius'
            ? { ...hazard, radius: clampNumber(distanceBetween(hazard.position, currentPoint), 40, 2400) }
            : hazard,
        ),
      };
    case 'soundSource':
      return {
        ...next,
        soundSources: next.soundSources.map((source) =>
          source.id === selection.id && handle === 'radius'
            ? { ...source, radius: clampNumber(distanceBetween(source.position, currentPoint), 60, 3000) }
            : source,
        ),
      };
    default:
      return next;
  }
}

export function validateEditableMap(map: MysteryRoomMapDefinition): string[] {
  const issues: string[] = [];
  if (map.width < 640 || map.height < 480) {
    issues.push('Размер карты должен быть не меньше 640x480.');
  }
  if (!map.rooms.length) {
    issues.push('Нужна хотя бы одна зона/комната.');
  }
  if (!map.rooms.some((room) => room.id === map.spawnZoneId)) {
    issues.push('spawnZoneId должен ссылаться на существующую комнату.');
  }
  if (!map.rooms.some((room) => room.floor.some((floor) => rectContainsPoint(floor, map.spawn)))) {
    issues.push('Точка старта должна находиться внутри floor-геометрии.');
  }
  normalizeEditorSpawnSlots(map).forEach((slot, index) => {
    const point = { x: slot.x, y: slot.y };
    if (!map.rooms.some((room) => room.floor.some((floor) => rectContainsPoint(floor, point)))) {
      issues.push(`Точка спавна игрока ${index + 1} должна находиться внутри floor-геометрии.`);
    }
  });
  map.npcs.forEach((npc) => {
    if (!map.rooms.some((room) => room.floor.some((floor) => rectContainsPoint(floor, npc.spawn)))) {
      issues.push(`Актер ${npc.id} должен находиться внутри floor-геометрии.`);
    }
  });
  const lightIds = new Set(map.lights.map((light) => light.id));
  const doorIds = new Set(map.doors.map((door) => door.id));
  const soundSourceIds = new Set(map.soundSources.map((source) => source.id));
  for (const trigger of map.triggers) {
    for (const action of trigger.actions) {
      if (
        (action.kind === 'toggle_light' || action.kind === 'set_light' || action.kind === 'set_light_flicker')
        && action.lightIds.some((id) => !lightIds.has(id))
      ) {
        issues.push(`Триггер ${trigger.id} ссылается на несуществующий свет.`);
      }
      if ((action.kind === 'open_door' || action.kind === 'close_door') && action.doorIds.some((id) => !doorIds.has(id))) {
        issues.push(`Триггер ${trigger.id} ссылается на несуществующую дверь.`);
      }
      if (action.kind === 'play_sound' && action.soundSourceIds.some((id) => !soundSourceIds.has(id))) {
        issues.push(`Триггер ${trigger.id} ссылается на несуществующий источник звука.`);
      }
    }
  }
  return issues;
}

export function formatMysteryRoomPublishIssue(issue: string): string {
  const cleaned = issue.trim().replace(/\s+/g, ' ').replace(/[.。]+$/, '');
  if (!cleaned) return 'Проверьте карту и повторите публикацию.';
  if (/Размер карты должен быть не меньше/i.test(cleaned)) return 'Увеличьте размер карты минимум до 640x480.';
  if (/Размер карты слишком большой|Размер черновика слишком большой/i.test(cleaned)) return 'Уменьшите карту до лимита редактора.';
  if (/Нужна хотя бы одна зона|нет floor-геометрии/i.test(cleaned)) return 'Добавьте хотя бы одну комнату с областью пола.';
  if (/spawnZoneId/i.test(cleaned)) return 'Выберите существующую комнату для стартовой зоны.';
  if (/Точка старта|Spawn slot/i.test(cleaned)) return 'Разместите стартовые точки внутри проходной области пола.';
  if (/nav graph/i.test(cleaned)) return 'Добавьте навигационный граф для ботов и серверного запуска.';
  if (/Nav node/i.test(cleaned)) return 'Переместите узлы навигации внутрь комнат.';
  if (/Nav edge/i.test(cleaned)) return 'Проверьте связи навигации между существующими узлами и дверями.';
  if (/Дверь .*ссылается на несуществующую комнату/i.test(cleaned)) return 'Привяжите двери к существующим комнатам.';
  if (/Дверь .*требует пропуск|Дверь .*ссылается на несуществующий пропуск/i.test(cleaned)) return 'Проверьте пропуска у дверей: нужный предмет должен существовать на карте.';
  if (/Триггер .*ссылается/i.test(cleaned)) return `${cleaned}.`;
  if (/Для текущих контрактов нужен interactable/i.test(cleaned)) return 'Добавьте обязательные интерактивные объекты для игровых задач.';
  return `${cleaned}.`;
}

export function selectedEntityLabel(map: MysteryRoomMapDefinition, selection: MysteryRoomEditorSelection | null): string {
  if (!selection) return 'Ничего не выбрано';
  switch (selection.type) {
    case 'room':
      return map.rooms.find((room) => room.id === selection.id)?.title ?? selection.id;
    case 'door':
      return map.doors.find((door) => door.id === selection.id)?.label ?? map.doors.find((door) => door.id === selection.id)?.id ?? selection.id;
    case 'customObject':
      return map.customObjects.find((object) => object.id === selection.id)?.label ?? selection.id;
    case 'npc':
      return map.npcs.find((npc) => npc.id === selection.id)?.name ?? selection.id;
    case 'light':
      return map.lights.find((light) => light.id === selection.id)?.label ?? map.lights.find((light) => light.id === selection.id)?.id ?? selection.id;
    case 'interactable':
      return map.interactables.find((entry) => entry.id === selection.id)?.label ?? selection.id;
    case 'trigger':
      return map.triggers.find((trigger) => trigger.id === selection.id)?.label ?? selection.id;
    case 'hazard':
      return map.hazards.find((hazard) => hazard.id === selection.id)?.id ?? selection.id;
    case 'incidentAnchor':
      return map.incidentAnchors.find((anchor) => anchor.id === selection.id)?.id ?? selection.id;
    case 'workAnchor':
      return map.workAnchors.find((anchor) => anchor.id === selection.id)?.id ?? selection.id;
    case 'soundSource':
      return map.soundSources.find((source) => source.id === selection.id)?.label ?? selection.id;
    case 'navNode':
      return map.navNodes.find((node) => node.id === selection.id)?.id ?? selection.id;
    case 'spawn':
      return `Игрок ${spawnSlotIndexFromSelectionId(selection.id) + 1}`;
    case 'wall':
      return `Стена ${Number(selection.id) + 1}`;
    default:
      return selection.id;
  }
}

export const MYSTERY_ROOM_EDITOR_HAZARD_KINDS: MysteryRoomHazardDefinition['kind'][] = ['smoke', 'sparks'];
export const MYSTERY_ROOM_EDITOR_INCIDENT_KINDS: MysteryRoomIncidentAnchorKind[] = [
  'alarm',
  'fire',
  'door_failure',
  'reactor_fault',
  'server_fault',
  'injured_person',
];
export const MYSTERY_ROOM_EDITOR_WORK_ANCHOR_KINDS: MysteryRoomAnchorKind[] = [
  'checkpoint',
  'workstation',
  'experiment',
  'medical_station',
  'maintenance',
  'standby',
  'restroom',
  'break_room',
  'armory',
  'patrol_point',
  'medic_work',
  'engineer_work',
  'guard_work',
  'operator_work',
  'evacuation',
  'breakdown',
];
export const MYSTERY_ROOM_EDITOR_NPC_ROLES: MysteryRoomNpcRole[] = ['guard', 'scientist', 'technician', 'medic', 'operator'];
export const MYSTERY_ROOM_EDITOR_NPC_FACTIONS: MysteryRoomNpcFaction[] = ['ally', 'staff', 'neutral', 'hostile'];
export const MYSTERY_ROOM_EDITOR_NPC_BEHAVIOR_PROFILES: MysteryRoomNpcBehaviorProfile[] = [
  'civilian',
  'guard',
  'medic',
  'technician',
  'operator',
  'zombie',
  'boss',
  'custom',
];

export function numberToHexColor(color: number | undefined, fallback = '#38bdf8'): string {
  if (typeof color !== 'number' || !Number.isFinite(color)) {
    return fallback;
  }
  return `#${Math.max(0, Math.min(0xffffff, Math.round(color))).toString(16).padStart(6, '0')}`;
}

export function hexColorToNumber(value: string, fallback = 0x38bdf8): number {
  const normalized = value.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return fallback;
  }
  return Number.parseInt(normalized, 16);
}

export function formatMapAssetBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}

export type MysteryRoomMapAssetKind = 'image' | 'audio';

export function normalizeMysteryRoomMapAssetExtension(value: string): string {
  const extension = value.trim().toLowerCase().replace(/^\.+/, '');
  return extension === 'waw' ? 'wav' : extension;
}

export function classifyMysteryRoomMapAsset(
  file: { name?: string | null; type?: string | null },
): MysteryRoomMapAssetKind | null {
  const mimeType = (file.type ?? '').trim().toLowerCase();
  const rawExtension = normalizeMysteryRoomMapAssetExtension((file.name ?? '').split('.').pop() ?? '');
  const imageExtensions = new Set<string>(MR_MAP_IMAGE_ASSET_EXTENSIONS);
  const audioExtensions = new Set<string>(MR_MAP_AUDIO_ASSET_EXTENSIONS);

  if (imageExtensions.has(rawExtension)) {
    return 'image';
  }
  if (audioExtensions.has(rawExtension)) {
    return 'audio';
  }
  if (['image/png', 'image/gif', 'image/webp', 'image/jpeg'].includes(mimeType)) {
    return 'image';
  }
  if (
    [
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/x-wav',
      'audio/wave',
      'audio/vnd.wave',
      'audio/ogg',
      'application/ogg',
      'audio/opus',
      'audio/mp4',
      'audio/x-m4a',
      'audio/aac',
      'audio/x-aac',
      'audio/webm',
      'video/webm',
    ].includes(mimeType)
  ) {
    return 'audio';
  }
  return null;
}

export function isMysteryRoomMapImageAsset(asset: { name?: string | null; type?: string | null }): boolean {
  return classifyMysteryRoomMapAsset(asset) === 'image';
}

export function isMysteryRoomMapAudioAsset(asset: { name?: string | null; type?: string | null }): boolean {
  return classifyMysteryRoomMapAsset(asset) === 'audio';
}

function resolveZoneIdAtPoint(map: MysteryRoomMapDefinition, point: MysteryRoomVec2): string {
  return map.rooms.find((room) => room.floor.some((floor) => rectContainsPoint(floor, point)))?.id ?? map.spawnZoneId ?? map.rooms[0]?.id ?? 'room';
}

function clampRectToMap(rect: MysteryRoomRect, map: MysteryRoomMapDefinition): MysteryRoomRect {
  const width = Math.max(8, Math.round(rect.width));
  const height = Math.max(8, Math.round(rect.height));
  return {
    x: Math.max(0, Math.round(rect.x)),
    y: Math.max(0, Math.round(rect.y)),
    width,
    height,
  };
}

function offsetRect(rect: MysteryRoomRect, dx: number, dy: number): MysteryRoomRect {
  return {
    ...rect,
    x: Math.round(rect.x + dx),
    y: Math.round(rect.y + dy),
  };
}

function moveCustomObject(object: MysteryRoomCustomObjectDefinition, dx: number, dy: number): MysteryRoomCustomObjectDefinition {
  const rect = offsetRect(object.rect, dx, dy);
  if (object.collision.kind === 'rect') {
    return {
      ...object,
      rect,
      collision: { ...object.collision, rect: offsetRect(object.collision.rect, dx, dy) },
    };
  }
  if (object.collision.kind === 'circle') {
    return {
      ...object,
      rect,
      collision: {
        ...object.collision,
        center: { x: object.collision.center.x + dx, y: object.collision.center.y + dy },
      },
    };
  }
  if (object.collision.kind === 'polygon') {
    return {
      ...object,
      rect,
      collision: {
        ...object.collision,
        points: object.collision.points.map((point) => ({ x: point.x + dx, y: point.y + dy })),
      },
    };
  }
  return { ...object, rect };
}

function isSizeAction(action: MysteryRoomEditorTransformAction): boolean {
  return action === 'length_decrease' || action === 'length_increase' || action === 'thickness_decrease' || action === 'thickness_increase';
}

function isRadiusAction(action: MysteryRoomEditorTransformAction): boolean {
  return action === 'radius_decrease' || action === 'radius_increase';
}

function resizeRectByAction(
  rect: MysteryRoomRect,
  action: MysteryRoomEditorTransformAction,
  limits: { minWidth: number; minHeight: number },
): MysteryRoomRect {
  const widthDelta = action === 'length_increase' ? 32 : action === 'length_decrease' ? -32 : 0;
  const heightDelta = action === 'thickness_increase' ? 24 : action === 'thickness_decrease' ? -24 : 0;
  return resizeRectFromCenter(
    rect,
    Math.max(limits.minWidth, rect.width + widthDelta),
    Math.max(limits.minHeight, rect.height + heightDelta),
  );
}

function resizeRectFromCenter(rect: MysteryRoomRect, width: number, height: number): MysteryRoomRect {
  const center = rectCenter(rect);
  return {
    x: Math.round(center.x - width / 2),
    y: Math.round(center.y - height / 2),
    width: Math.round(width),
    height: Math.round(height),
  };
}

function resizeCustomObjectByAction(
  object: MysteryRoomCustomObjectDefinition,
  action: MysteryRoomEditorTransformAction,
  map: MysteryRoomMapDefinition,
): MysteryRoomCustomObjectDefinition {
  const rect = clampRectToMap(resizeRectByAction(object.rect, action, { minWidth: 24, minHeight: 24 }), map);
  return withSyncedObjectCollision(object, rect);
}

export function isCustomObjectEdgeHandle(handle: MysteryRoomEditorTransformHandle): handle is `edge:${number}` {
  return handle.startsWith('edge:');
}

export function customObjectEdgeHandleIndex(handle: `edge:${number}`): number {
  return Math.max(0, Number.parseInt(handle.slice(5), 10) || 0);
}

export function customObjectEdgeHandle(edgeIndex: number): `edge:${number}` {
  return `edge:${Math.max(0, Math.round(edgeIndex))}`;
}

export function isRectSideHandle(handle: MysteryRoomEditorTransformHandle): handle is `side:${MysteryRoomEditorRectSide}` {
  return handle === 'side:top' || handle === 'side:right' || handle === 'side:bottom' || handle === 'side:left';
}

export function rectSideFromHandle(handle: `side:${MysteryRoomEditorRectSide}`): MysteryRoomEditorRectSide {
  return handle.slice(5) as MysteryRoomEditorRectSide;
}

export function rectSideHandle(side: MysteryRoomEditorRectSide): `side:${MysteryRoomEditorRectSide}` {
  return `side:${side}`;
}

export function isWallEndpointHandle(handle: MysteryRoomEditorTransformHandle): handle is `wallEndpoint:${MysteryRoomEditorWallEndpoint}` {
  return handle === 'wallEndpoint:start' || handle === 'wallEndpoint:end';
}

export function wallEndpointFromHandle(handle: `wallEndpoint:${MysteryRoomEditorWallEndpoint}`): MysteryRoomEditorWallEndpoint {
  return handle.slice('wallEndpoint:'.length) as MysteryRoomEditorWallEndpoint;
}

export function wallEndpointHandle(endpoint: MysteryRoomEditorWallEndpoint): `wallEndpoint:${MysteryRoomEditorWallEndpoint}` {
  return `wallEndpoint:${endpoint}`;
}

function dragCustomObjectShapeEdge(
  object: MysteryRoomCustomObjectDefinition,
  edgeIndex: number,
  startPoint: MysteryRoomVec2,
  currentPoint: MysteryRoomVec2,
  map: MysteryRoomMapDefinition,
): MysteryRoomCustomObjectDefinition {
  const basePoints = getCustomObjectShapePoints(object);
  const pointCount = basePoints.length;
  if (pointCount === 0) {
    return object;
  }

  const selectedIndex = Math.max(0, Math.min(pointCount - 1, edgeIndex));
  const nextIndex = pointCount > 1 ? (selectedIndex + 1) % pointCount : selectedIndex;
  const localDelta = worldDeltaToObjectLocalUnit(object, {
    x: currentPoint.x - startPoint.x,
    y: currentPoint.y - startPoint.y,
  });
  const normal = customObjectEdgeLocalNormal(basePoints, selectedIndex, nextIndex);
  const amount = localDelta.x * normal.x + localDelta.y * normal.y;
  if (!Number.isFinite(amount) || Math.abs(amount) < 0.001) {
    return object;
  }

  const points = basePoints.map((point, index) =>
    index === selectedIndex || index === nextIndex
      ? {
          x: point.x + normal.x * amount,
          y: point.y + normal.y * amount,
        }
      : { ...point },
  );
  return normalizeCustomObjectPolygonGeometry(object, points, map);
}

function worldDeltaToObjectLocalUnit(
  object: MysteryRoomCustomObjectDefinition,
  delta: MysteryRoomVec2,
): MysteryRoomVec2 {
  const radians = -((object.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: (delta.x * cos - delta.y * sin) / Math.max(1, object.rect.width),
    y: (delta.x * sin + delta.y * cos) / Math.max(1, object.rect.height),
  };
}

function customObjectEdgeLocalNormal(
  points: MysteryRoomVec2[],
  index: number,
  nextIndex: number,
): MysteryRoomVec2 {
  if (points.length === 1) {
    return { x: 1, y: 0 };
  }
  const start = points[index] ?? points[0];
  const end = points[nextIndex] ?? points[points.length - 1] ?? start;
  const edge = { x: end.x - start.x, y: end.y - start.y };
  const clockwiseArea = polygonSignedArea(points) < 0;
  const normal = clockwiseArea ? { x: edge.y, y: -edge.x } : { x: -edge.y, y: edge.x };
  const length = Math.hypot(normal.x, normal.y);
  if (length < 0.0001) {
    return { x: 1, y: 0 };
  }
  return { x: normal.x / length, y: normal.y / length };
}

function polygonSignedArea(points: MysteryRoomVec2[]): number {
  return points.reduce((area, point, index) => {
    const next = points[(index + 1) % points.length] ?? point;
    return area + point.x * next.y - next.x * point.y;
  }, 0) / 2;
}

function normalizeCustomObjectPolygonGeometry(
  object: MysteryRoomCustomObjectDefinition,
  points: MysteryRoomVec2[],
  map: MysteryRoomMapDefinition,
): MysteryRoomCustomObjectDefinition {
  const minX = Math.min(...points.map((point) => point.x));
  const maxX = Math.max(...points.map((point) => point.x));
  const minY = Math.min(...points.map((point) => point.y));
  const maxY = Math.max(...points.map((point) => point.y));
  const widthSpan = Math.max(0.08, maxX - minX);
  const heightSpan = Math.max(0.08, maxY - minY);
  const centerShiftUnit = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  const rotatedShift = rotateLocalVector(
    centerShiftUnit.x * object.rect.width,
    centerShiftUnit.y * object.rect.height,
    object.rotation ?? 0,
  );
  const oldCenter = rectCenter(object.rect);
  const nextWidth = Math.max(24, object.rect.width * widthSpan);
  const nextHeight = Math.max(24, object.rect.height * heightSpan);
  const rect = clampRectToMap(
    {
      x: oldCenter.x + rotatedShift.x - nextWidth / 2,
      y: oldCenter.y + rotatedShift.y - nextHeight / 2,
      width: nextWidth,
      height: nextHeight,
    },
    map,
  );
  const normalizedPoints = points.map((point) => ({
    x: Math.round(((point.x - centerShiftUnit.x) / widthSpan) * 1000) / 1000,
    y: Math.round(((point.y - centerShiftUnit.y) / heightSpan) * 1000) / 1000,
  }));
  return withSyncedObjectCollision(
    {
      ...object,
      shape: {
        kind: 'polygon',
        points: normalizedPoints,
      },
    },
    rect,
  );
}

function resizeCustomObjectByDrag(
  object: MysteryRoomCustomObjectDefinition,
  handle: MysteryRoomEditorTransformHandle,
  startPoint: MysteryRoomVec2,
  currentPoint: MysteryRoomVec2,
  map: MysteryRoomMapDefinition,
): MysteryRoomCustomObjectDefinition {
  const radians = object.rotation * Math.PI / 180;
  const lengthAxis = { x: Math.cos(radians), y: Math.sin(radians) };
  const thicknessAxis = { x: -Math.sin(radians), y: Math.cos(radians) };
  const width = Math.max(24, object.rect.width + (handle === 'length' ? 2 * projectDeltaOntoAxis(startPoint, currentPoint, lengthAxis) : 0));
  const height = Math.max(24, object.rect.height + (handle === 'thickness' ? 2 * projectDeltaOntoAxis(startPoint, currentPoint, thicknessAxis) : 0));
  const rect = clampRectToMap(resizeRectFromCenter(object.rect, width, height), map);
  return withSyncedObjectCollision(object, rect);
}

function resizeCustomObjectSideByDrag(
  object: MysteryRoomCustomObjectDefinition,
  side: MysteryRoomEditorRectSide,
  startPoint: MysteryRoomVec2,
  currentPoint: MysteryRoomVec2,
  map: MysteryRoomMapDefinition,
): MysteryRoomCustomObjectDefinition {
  const radians = (object.rotation ?? 0) * Math.PI / 180;
  const lengthAxis = { x: Math.cos(radians), y: Math.sin(radians) };
  const thicknessAxis = { x: -Math.sin(radians), y: Math.cos(radians) };
  const center = rectCenter(object.rect);
  const minWidth = 24;
  const minHeight = 24;
  let width = object.rect.width;
  let height = object.rect.height;
  let centerShift = { x: 0, y: 0 };

  if (side === 'right' || side === 'left') {
    const direction = side === 'right' ? 1 : -1;
    const rawDelta = projectDeltaOntoAxis(startPoint, currentPoint, lengthAxis) * direction;
    const delta = Math.max(minWidth - object.rect.width, rawDelta);
    width = object.rect.width + delta;
    centerShift = {
      x: lengthAxis.x * direction * delta / 2,
      y: lengthAxis.y * direction * delta / 2,
    };
  } else {
    const direction = side === 'bottom' ? 1 : -1;
    const rawDelta = projectDeltaOntoAxis(startPoint, currentPoint, thicknessAxis) * direction;
    const delta = Math.max(minHeight - object.rect.height, rawDelta);
    height = object.rect.height + delta;
    centerShift = {
      x: thicknessAxis.x * direction * delta / 2,
      y: thicknessAxis.y * direction * delta / 2,
    };
  }

  const rect = clampRectToMap(
    {
      x: center.x + centerShift.x - width / 2,
      y: center.y + centerShift.y - height / 2,
      width,
      height,
    },
    map,
  );
  return withSyncedObjectCollision(object, rect);
}

function withSyncedObjectCollision(object: MysteryRoomCustomObjectDefinition, rect: MysteryRoomRect): MysteryRoomCustomObjectDefinition {
  if (object.collision.kind !== 'none' && object.shape?.kind === 'polygon') {
    return { ...object, rect, collision: createCustomObjectShapeCollision(object, rect) };
  }
  if (object.collision.kind === 'rect') {
    return { ...object, rect, collision: { ...object.collision, rect } };
  }
  if (object.collision.kind === 'circle') {
    return {
      ...object,
      rect,
      collision: {
        ...object.collision,
        center: rectCenter(rect),
        radius: Math.max(8, Math.round(Math.min(rect.width, rect.height) / 2)),
      },
    };
  }
  if (object.collision.kind === 'polygon') {
    return { ...object, rect, collision: createCustomObjectShapeCollision(object, rect) };
  }
  return { ...object, rect };
}

function resizeAxisAlignedRectByDrag(
  rect: MysteryRoomRect,
  handle: MysteryRoomEditorTransformHandle,
  startPoint: MysteryRoomVec2,
  currentPoint: MysteryRoomVec2,
  limits: { minWidth: number; minHeight: number },
  map: MysteryRoomMapDefinition,
): MysteryRoomRect {
  const width = Math.max(limits.minWidth, rect.width + (handle === 'length' ? 2 * (currentPoint.x - startPoint.x) : 0));
  const height = Math.max(limits.minHeight, rect.height + (handle === 'thickness' ? 2 * (currentPoint.y - startPoint.y) : 0));
  return clampRectToMap(resizeRectFromCenter(rect, width, height), map);
}

function resizeAxisAlignedRectSideByDrag(
  rect: MysteryRoomRect,
  side: MysteryRoomEditorRectSide,
  startPoint: MysteryRoomVec2,
  currentPoint: MysteryRoomVec2,
  limits: { minWidth: number; minHeight: number },
  map: MysteryRoomMapDefinition,
): MysteryRoomRect {
  const dx = currentPoint.x - startPoint.x;
  const dy = currentPoint.y - startPoint.y;
  let next = { ...rect };

  if (side === 'left') {
    const maxX = rect.x + rect.width - limits.minWidth;
    next.x = Math.min(rect.x + dx, maxX);
    next.width = rect.x + rect.width - next.x;
  } else if (side === 'right') {
    next.width = Math.max(limits.minWidth, rect.width + dx);
  } else if (side === 'top') {
    const maxY = rect.y + rect.height - limits.minHeight;
    next.y = Math.min(rect.y + dy, maxY);
    next.height = rect.y + rect.height - next.y;
  } else {
    next.height = Math.max(limits.minHeight, rect.height + dy);
  }

  return clampRectToMap(next, map);
}

function resizeDoorPanel(
  door: MysteryRoomDoorDefinition,
  action: MysteryRoomEditorTransformAction,
  map: MysteryRoomMapDefinition,
): MysteryRoomDoorDefinition {
  const lengthDelta = action === 'length_increase' ? 32 : action === 'length_decrease' ? -32 : 0;
  const thicknessDelta = action === 'thickness_increase' ? 10 : action === 'thickness_decrease' ? -10 : 0;
  const isHorizontal = door.orientation === 'horizontal';
  const width = Math.max(24, door.panel.width + (isHorizontal ? lengthDelta : thicknessDelta));
  const height = Math.max(12, door.panel.height + (isHorizontal ? thicknessDelta : lengthDelta));
  const panel = clampRectToMap(resizeRectFromCenter(door.panel, width, height), map);
  return { ...door, panel, trigger: fitDoorTriggerToPanel(door.trigger, panel, map) };
}

function resizeDoorPanelByDrag(
  door: MysteryRoomDoorDefinition,
  handle: MysteryRoomEditorTransformHandle,
  startPoint: MysteryRoomVec2,
  currentPoint: MysteryRoomVec2,
  map: MysteryRoomMapDefinition,
): MysteryRoomDoorDefinition {
  const lengthAxis = door.orientation === 'horizontal' ? { x: 1, y: 0 } : { x: 0, y: 1 };
  const thicknessAxis = door.orientation === 'horizontal' ? { x: 0, y: 1 } : { x: 1, y: 0 };
  const lengthDelta = handle === 'length' ? 2 * projectDeltaOntoAxis(startPoint, currentPoint, lengthAxis) : 0;
  const thicknessDelta = handle === 'thickness' ? 2 * projectDeltaOntoAxis(startPoint, currentPoint, thicknessAxis) : 0;
  const width = Math.max(24, door.panel.width + (door.orientation === 'horizontal' ? lengthDelta : thicknessDelta));
  const height = Math.max(12, door.panel.height + (door.orientation === 'horizontal' ? thicknessDelta : lengthDelta));
  const panel = clampRectToMap(resizeRectFromCenter(door.panel, width, height), map);
  return { ...door, panel, trigger: fitDoorTriggerToPanel(door.trigger, panel, map) };
}

function resizeDoorPanelSideByDrag(
  door: MysteryRoomDoorDefinition,
  side: MysteryRoomEditorRectSide,
  startPoint: MysteryRoomVec2,
  currentPoint: MysteryRoomVec2,
  map: MysteryRoomMapDefinition,
): MysteryRoomDoorDefinition {
  const panel = resizeAxisAlignedRectSideByDrag(door.panel, side, startPoint, currentPoint, { minWidth: 24, minHeight: 12 }, map);
  return { ...door, panel, trigger: fitDoorTriggerToPanel(door.trigger, panel, map) };
}

function rotateDoorPanel(door: MysteryRoomDoorDefinition, map: MysteryRoomMapDefinition): MysteryRoomDoorDefinition {
  const panel = clampRectToMap(resizeRectFromCenter(door.panel, door.panel.height, door.panel.width), map);
  return {
    ...door,
    orientation: door.orientation === 'horizontal' ? 'vertical' : 'horizontal',
    panel,
    trigger: fitDoorTriggerToPanel(resizeRectFromCenter(door.trigger, door.trigger.height, door.trigger.width), panel, map),
  };
}

function fitDoorTriggerToPanel(trigger: MysteryRoomRect, panel: MysteryRoomRect, map: MysteryRoomMapDefinition): MysteryRoomRect {
  const center = rectCenter(panel);
  const width = Math.max(trigger.width, panel.width + 88);
  const height = Math.max(trigger.height, panel.height + 88);
  return clampRectToMap(
    {
      x: center.x - width / 2,
      y: center.y - height / 2,
      width,
      height,
    },
    map,
  );
}

function resizeWallSegment(wall: MysteryRoomSegment, deltaLength: number, map: MysteryRoomMapDefinition): MysteryRoomSegment {
  const center = segmentCenter(wall);
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const length = Math.max(48, Math.hypot(dx, dy) + deltaLength);
  const currentLength = Math.max(1, Math.hypot(dx, dy));
  const unit = { x: dx / currentLength, y: dy / currentLength };
  return {
    ...wall,
    start: clampPointToMap({ x: center.x - unit.x * length / 2, y: center.y - unit.y * length / 2 }, map),
    end: clampPointToMap({ x: center.x + unit.x * length / 2, y: center.y + unit.y * length / 2 }, map),
  };
}

function dragWallEndpoint(
  wall: MysteryRoomSegment,
  endpoint: MysteryRoomEditorWallEndpoint,
  startPoint: MysteryRoomVec2,
  currentPoint: MysteryRoomVec2,
  map: MysteryRoomMapDefinition,
): MysteryRoomSegment {
  const delta = { x: currentPoint.x - startPoint.x, y: currentPoint.y - startPoint.y };
  const nextPoint = clampPointToMap(
    {
      x: wall[endpoint].x + delta.x,
      y: wall[endpoint].y + delta.y,
    },
    map,
  );
  const otherPoint = endpoint === 'start' ? wall.end : wall.start;
  if (Math.hypot(nextPoint.x - otherPoint.x, nextPoint.y - otherPoint.y) < 48) {
    const axis = wallAxis(wall);
    const direction = endpoint === 'start' ? -1 : 1;
    return {
      ...wall,
      [endpoint]: clampPointToMap(
        {
          x: otherPoint.x + axis.x * 48 * direction,
          y: otherPoint.y + axis.y * 48 * direction,
        },
        map,
      ),
    };
  }
  return { ...wall, [endpoint]: nextPoint };
}

function rotateWallSegment(wall: MysteryRoomSegment, degrees: number, map: MysteryRoomMapDefinition): MysteryRoomSegment {
  const center = segmentCenter(wall);
  return {
    ...wall,
    start: clampPointToMap(rotatePointAround(wall.start, center, degrees), map),
    end: clampPointToMap(rotatePointAround(wall.end, center, degrees), map),
  };
}

function wallAxis(wall: MysteryRoomSegment): MysteryRoomVec2 {
  const dx = wall.end.x - wall.start.x;
  const dy = wall.end.y - wall.start.y;
  const length = Math.max(1, Math.hypot(dx, dy));
  return { x: dx / length, y: dy / length };
}

function segmentCenter(segment: MysteryRoomSegment): MysteryRoomVec2 {
  return {
    x: (segment.start.x + segment.end.x) / 2,
    y: (segment.start.y + segment.end.y) / 2,
  };
}

function projectDeltaOntoAxis(startPoint: MysteryRoomVec2, currentPoint: MysteryRoomVec2, axis: MysteryRoomVec2): number {
  return (currentPoint.x - startPoint.x) * axis.x + (currentPoint.y - startPoint.y) * axis.y;
}

function distanceBetween(a: MysteryRoomVec2, b: MysteryRoomVec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function angleDeltaAround(center: MysteryRoomVec2, startPoint: MysteryRoomVec2, currentPoint: MysteryRoomVec2): number {
  const startDistance = distanceBetween(center, startPoint);
  const currentDistance = distanceBetween(center, currentPoint);
  if (startDistance < 1 || currentDistance < 1) {
    return 0;
  }
  const startAngle = Math.atan2(startPoint.y - center.y, startPoint.x - center.x);
  const currentAngle = Math.atan2(currentPoint.y - center.y, currentPoint.x - center.x);
  return normalizeAngleDelta((currentAngle - startAngle) * 180 / Math.PI);
}

function normalizeAngleDelta(degrees: number): number {
  let value = degrees;
  while (value > 180) value -= 360;
  while (value < -180) value += 360;
  return value;
}

function rotatePointAround(point: MysteryRoomVec2, center: MysteryRoomVec2, degrees: number): MysteryRoomVec2 {
  const radians = degrees * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
}

function rotateLocalVector(x: number, y: number, degrees: number): MysteryRoomVec2 {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: x * cos - y * sin,
    y: x * sin + y * cos,
  };
}

function clampPointToMap(point: MysteryRoomVec2, map: MysteryRoomMapDefinition): MysteryRoomVec2 {
  return {
    x: Math.max(0, Math.round(point.x)),
    y: Math.max(0, Math.round(point.y)),
  };
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Math.round(value)));
}

function normalizeDegrees(value: number): number {
  const next = value % 360;
  return Math.round(next < 0 ? next + 360 : next);
}

function createTemplateObject(
  id: string,
  zoneId: string,
  label: string,
  kind: MysteryRoomCustomObjectKind,
  rect: MysteryRoomRect,
  color: number,
): MysteryRoomCustomObjectDefinition {
  return {
    id,
    kind,
    zoneId,
    rect,
    rotation: 0,
    color,
    shape: createRegularCustomObjectShape(4),
    layer: defaultEditorLayerForCustomObjectKind(kind),
    accentColor: 0xf8fafc,
    label,
    collision: { kind: 'rect', rect: { ...rect }, blocksMovement: true, blocksSight: kind === 'wall' || kind === 'equipment' },
    collisionEnabled: true,
    physicsEnabled: false,
    weightKg: defaultEditorWeightForCustomObjectKind(kind),
    blocksSight: kind === 'wall' || kind === 'equipment',
    visibleThroughWalls: false,
    interactive: kind === 'equipment',
    tags: [kind],
  };
}

function normalizeEditorObjectWeight(value: number | null | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 25;
  }
  return Math.max(0, Math.round(value * 10) / 10);
}

function defaultEditorWeightForCustomObjectKind(kind: MysteryRoomCustomObjectKind): number {
  switch (kind) {
    case 'wall':
    case 'door':
      return 300;
    case 'equipment':
      return 120;
    case 'table':
      return 35;
    case 'badge':
    case 'weapon':
      return 1;
    default:
      return 25;
  }
}

function roundPoint(point: MysteryRoomVec2): MysteryRoomVec2 {
  return { x: Math.round(point.x), y: Math.round(point.y) };
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9а-яё]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 42) || 'map'
  );
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
